import { Component, ChangeDetectionStrategy, signal, computed, effect, inject, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { PropertyTaxRateService } from './services/property-tax-rate.service';
import { UserMenuComponent } from './components/user-menu/user-menu.component';
import { ExpiringSubscriptionBannerComponent } from './components/expiring-subscription-banner/expiring-subscription-banner.component';
import { FooterComponent } from './components/footer/footer.component';
import { CalculationStorageService, type CalculationData } from './services/calculation-storage.service';
import { SaveCalculationDialogComponent } from './components/save-calculation-dialog/save-calculation-dialog.component';
import { LoadCalculationDialogComponent } from './components/load-calculation-dialog/load-calculation-dialog.component';
import { RenameCalculationDialogComponent } from './components/rename-calculation-dialog/rename-calculation-dialog.component';
import { DeleteConfirmationDialogComponent } from './components/delete-confirmation-dialog/delete-confirmation-dialog.component';
import { DuplicateCalculationDialogComponent } from './components/duplicate-calculation-dialog/duplicate-calculation-dialog.component';
import { SubscriptionExpiredModalComponent } from './components/subscription-expired-modal/subscription-expired-modal.component';
import { ExportCalculationDialogComponent } from './components/export-calculation-dialog/export-calculation-dialog.component';
import { ExportComparisonDialogComponent } from './components/export-comparison-dialog/export-comparison-dialog.component';
import { AuthService } from './services/auth.service';
import { SubscriptionService } from './services/subscription.service';
import { AiService } from './services/ai.service';
import { CountrySearchService } from './services/country-search.service';
import { ToastService } from './services/toast.service';

interface NominatimResult {
  display_name: string;
  address?: {
    country?: string;
    city?: string;
    town?: string;
    village?: string;
    postcode?: string;
    road?: string;
    house_number?: string;
  };
  lat?: string;
  lon?: string;
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, UserMenuComponent, ExpiringSubscriptionBannerComponent, FooterComponent, SaveCalculationDialogComponent, LoadCalculationDialogComponent, RenameCalculationDialogComponent, DeleteConfirmationDialogComponent, DuplicateCalculationDialogComponent, SubscriptionExpiredModalComponent, ExportCalculationDialogComponent, ExportComparisonDialogComponent],
  templateUrl: './app.html',
  styleUrl: './app.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App {
  // Signals for costs calculations
  title = signal('ProTyco');
  subtitle = signal('PROPERTY TYCOON');

  private taxService = inject(PropertyTaxRateService);
  private calculationStorageService = inject(CalculationStorageService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private aiService = inject(AiService);
  private subscriptionService = inject(SubscriptionService);
  private countrySearchService = inject(CountrySearchService);
  private toastService = inject(ToastService);
  
  // Constant: Maximum amortization period (safety limit)
  private readonly MAX_AMORTIZATION_MONTHS = 1200; // 100 years
  
  // Help texts - single source of truth for all help content
  readonly helpTexts = {
    addressData: {
      title: 'Adressdaten',
      content: {
        country: '<strong>Land:</strong> Wählen Sie das Land, in dem sich die Immobilie befindet. Dies ist wichtig für die Berechnung von Steuersätzen.',
        city: '<strong>Stadt *:</strong> Der Ort beeinflusst die Grunderwerbsteuer. Unterschiedliche Bundesländer und Kommunen haben unterschiedliche Sätze. Anhand der Stadt wird automatisch die Grunderwerbsteuer ermittelt.',
        address: '<strong>Straße:</strong> Vollständige Straßenadresse zur Identifikation der Immobilie. Anhand der Adresse werden die erwarteten Mieteinnahmen ermittelt.',
        postalCode: '<strong>Postleitzahl:</strong> Postleitzahl des Objekts. Wird automatisch ausgefüllt basierend auf der gewählten Adresse und kann manuell angepasst werden.'
      }
    },
    purchaseCosts: {
      title: 'Kaufkosten',
      content: {
        purchasePrice: '<strong>Kaufpreis:</strong> Die Gesamtkaufpreissumme der Immobilie.',
        agentCosts: '<strong>Maklerkosten (€):</strong> Üblicherweise 3-8% des Kaufpreises. Werden oft zwischen Käufer und Verkäufer aufgeteilt.',
        agentCostsPercentage: '<strong>Maklerkosten (%):</strong> Prozentualer Anteil vom Kaufpreis. Bei Eingabe wird der €-Betrag automatisch berechnet und umgekehrt.',
        notaryCosts: '<strong>Notarkosten (€):</strong> Ca. 1-1,5% des Kaufpreises. Sind in Deutschland gesetzlich festgelegt.',
        notaryCostsPercentage: '<strong>Notarkosten (%):</strong> Prozentualer Anteil vom Kaufpreis. Standardwert ca. 1-1,5%. Bei Eingabe wird der €-Betrag automatisch berechnet.',
        landRegistryCosts: '<strong>Grundbuchamtkosten (€):</strong> Ca. 0,5% des Kaufpreises. Entstehen durch die Eintragung ins Grundbuch.',
        landRegistryCostsPercentage: '<strong>Grundbuchamtkosten (%):</strong> Prozentualer Anteil vom Kaufpreis. Standardwert ca. 0,5%. Bei Eingabe wird der €-Betrag automatisch berechnet.',
        acquisitionTax: '<strong>Grunderwerbsteuer (€):</strong> Regional unterschiedlich. Wird automatisch basierend auf der Stadt berechnet.',
        acquisitionTaxPercentage: '<strong>Grunderwerbsteuer (%):</strong> Prozentualer Anteil vom Kaufpreis. Variiert je nach Bundesland zwischen 3,5% und 6,5%. Wird automatisch basierend auf der Stadt ermittelt.',
        eigenkapital: '<strong>Eigenkapital:</strong> Der Betrag, den Sie selbst einzahlen. Höheres Eigenkapital = niedrigere Kredite und bessere Konditionen.',
        kreditbedarf: '<strong>Kreditbedarf (€):</strong> Der Kaufpreis abzüglich des Eigenkapitals. Der Betrag, den Sie finanzieren müssen.'
      }
    },
    objectIncome: {
      title: 'Objekt-Einnahmen',
      content: {
        objectSize: '<strong>Objekt-Größe in m²:</strong> Wohnfläche der Immobilie.',
        constructionYear: '<strong>Baujahr (optional):</strong> Baujahr des Gebäudes. Relevant für die Mietpreisbremse (§ 556d BGB) – Neubauten ab Baujahr 2014 sind ausgenommen (§ 556f BGB). Zusätzlich beeinflusst das Alter die Wertsteigerungsprognose (Neubau, Bestand, Altbau, Denkmalschutz). Wenn unbekannt, einfach leer lassen – die KI kalkuliert dann konservativ.',
        rentalPrice: '<strong>Mietpreis / m²:</strong> Durchschnittlicher Quadratmeterpreis für Vermietung.',
        monthlyRentalPrice: '<strong>Mieteinnahmen monatlich:</strong> Monatliche Mieteinnahmen aus der Immobilie.',
        yearlyRentalPrice: '<strong>Mieteinnahmen jährlich:</strong> Annualisierte Mieteinnahmen (monatlich × 12).',
        additionalMonthlyIncome: '<strong>Weitere Einnahmen monatlich:</strong> Zusätzliche monatliche Einnahmen wie Parkplätze, Nebenräume, etc.',
        additionalYearlyIncome: '<strong>Weitere Einnahmen jährlich:</strong> Annualisierte zusätzliche Einnahmen.',
        xfachVonKaufpreis: '<strong>x-fach von Kaufpreis (Jahre):</strong> Wie viele Jahre es dauern würde, bis die jährlichen Mieteinnahmen den Kaufpreis amortisieren. Berechnung: Kaufpreis ÷ Jährliche Mieteinnahmen. Niedrigere Werte sind besser. Beachte: Dies berücksichtigt keine Nebenkosten oder laufenden Ausgaben.',
        xfachVonGesamtKaufkosten: '<strong>x-fach von Gesamt-Kaufkosten (Jahre):</strong> Wie "x-fach von Kaufpreis", berücksichtigt aber zusätzlich alle Kaufnebenkosten (Makler, Grunderwerbsteuer, Notar, Grundbuchamtkosten, etc.). Realistischere Einschätzung der Amortisationsdauer.'
      }
    },
    financing: {
      title: 'Finanzierungs-Kosten',
      content: {
        kreditbedarf: '<strong>Kreditbedarf:</strong> Der zu finanzierende Betrag.',
        interestRate: '<strong>Jahreszins effektiv (%):</strong> Effektiver Jahreszins für das Darlehen, beinhaltet alle Kosten.',
        amortizationRate: '<strong>Anfängliche Tilgung (%):</strong> Jährliche Rückzahlungsquote.',
        monthlyRate: '<strong>Kreditrate / Monat (€):</strong> Monatliche Zahlungsrate.',
        interestBindingTerm: '<strong>Zinsbindung (Jahre):</strong> Zeitraum, in dem der Zinssatz gebunden ist.',
        annualExtraRepayment: '<strong>Jährliche Sondertilgung (€):</strong> Zusätzliche Zahlung zur schnelleren Rückzahlung.',
        totalAmortization: '<strong>Gesamt-Tilgung in Laufzeit (€):</strong> Summe aller Tilgungszahlungen.',
        totalInterest: '<strong>Gesamt-Zinsen in Laufzeit (€):</strong> Gesamte Zinskosten in der Bindungsperiode.',
        totalPayment: '<strong>Gesamtzahlung in Laufzeit (€):</strong> Tilgung + Zinsen in der Bindungsperiode.',
        remainingDebt: '<strong>Restschuld nach Zinsbindung (€):</strong> Verbleibende Schuld nach Ende der Bindungsperiode.',
        creditTerm: '<strong>Kredit Rückzahlungsdauer (Jahre):</strong> Geschätzte Gesamtdauer.',
        changedInterestRate: '<strong>Neuer Zinssatz nach Erst-Zinsbindung:</strong> Für Simulation mit verändertem Zinssatz.',
        totalCreditCost: '<strong>Gesamt-Kreditkosten, inkl. Restschuld (€):</strong> Gesamte Kreditkosten einschließlich der verbleibenden Restschuld nach der Zinsbindungsperiode.'
      }
    },
    runningExpenses: {
      title: 'Laufende Ausgaben',
      content: {
        adminCosts: '<strong>Verwaltungskosten:</strong> Hausverwaltung, Maklergebühren für Vermietung, etc.',
        taxCosts: '<strong>Steuern:</strong> Grundsteuer und sonstige Immobiliensteuern pro Jahr.',
        houseMoney: '<strong>Hausgeld:</strong> Nebenkosten, Betriebskosten für Gemeinschaftsflächen, Instandhaltungsrücklagen.',
        insurance: '<strong>Versicherungen:</strong> Gebäudeversicherung, Haftpflicht, etc.',
        repairCosts: '<strong>Reparaturen, Wartungskosten:</strong> Instandhaltung, Sanierung, regelmäßige Wartungen.',
        otherExpenses: '<strong>Sonstige Ausgaben:</strong> Weitere laufende Kosten.'
      }
    },
    incomeExcess: {
      title: 'Einnahmenüberschuß',
      content: {
        monthlyIncome: '<strong>Einnahmen monatlich:</strong> Gesamte monatliche Einnahmen aus Vermietung oder anderen Quellen.',
        monthlyFinancingExpenses: '<strong>Kreditausgaben monatlich:</strong> Monatliche Rate für das Darlehen (Tilgung + Zinsen).',
        monthlyOtherExpenses: '<strong>Laufende Ausgaben monatlich:</strong> Summe aller laufenden Ausgaben pro Monat.',
        monthlyTotalExpenses: '<strong>Gesamtausgaben monatlich:</strong> Summe aus Kreditausgaben und laufenden Ausgaben pro Monat.',
        monthlyIncomeExcess: '<strong>Einnahmenüberschuß monatlich:</strong> Restbetrag nach Abzug aller Ausgaben. Positiv = Gewinn, Negativ = Verlust.'
      }
    },
    scenarios: {
      title: 'Szenarien-Vergleich',
      content: {
        // Varianten-Beschreibungen
        variant1: '<strong>Variante 1 - Restschuld:</strong> Simulation bis Ende der Zinsbindung. Restschuld bleibt bestehen.',
        variant2: '<strong>Variante 2 - Bei gleichbleibendem Zinssatz:</strong> Komplette Tilgung mit unverändertem Zinssatz.',
        variant3: '<strong>Variante 3 - Veränderter Zinssatz:</strong> Komplette Tilgung mit verändertem Zinssatz nach Erst-Zinsbindung.',
        // Varianten-spezifische Texte für Kreditlaufzeit
        duration1: '<strong>Kreditlaufzeit:</strong> Dauer bis Ende Zinsbindung.',
        duration2: '<strong>Kreditlaufzeit:</strong> Dauer bis zur vollständigen Tilgung.',
        duration3: '<strong>Kreditlaufzeit:</strong> Dauer bis zur vollständigen Tilgung.',
        // Gemeinsame Felder
        totalIncome: '<strong>Mieteinnahmen gesamt:</strong> Summe aller Mieteinnahmen über die Kreditlaufzeit.',
        totalCreditCost: '<strong>− Kreditkosten gesamt:</strong> Zinsen + Tilgung über die gesamte Laufzeit.',
        runningExpenses: '<strong>− Lfd. Ausgaben gesamt:</strong> Verwaltung, Steuern, Hausgeld, Versicherungen etc. über die Laufzeit.',
        netCashflow: '<strong>= Netto-Cashflow:</strong> Mieteinnahmen minus alle Kosten (Kredit + lfd. Ausgaben).',
        appreciationRate: '<strong>Immobilien-Wertsteigerung:</strong> Jährliche prozentuale Wertsteigerung der Immobilie (editierbar).',
        propertyValue: '<strong>+ Immobilienwert:</strong> Kaufpreis × (1 + Wertsteigerung/100)^Laufzeit',
        // Varianten-spezifische Texte für Restschuld
        remainingDebt1: '<strong>− Restschuld:</strong> Verbleibende Kreditschuld am Ende der Zinsbindung.',
        remainingDebt2: '<strong>− Restschuld:</strong> 0,00 € - Der Kredit ist vollständig getilgt.',
        remainingDebt3: '<strong>− Restschuld:</strong> 0,00 € - Der Kredit ist vollständig getilgt.',
        // Gemeinsame Felder
        equity: '<strong>− Investiertes Eigenkapital:</strong> Ihr eingesetztes Eigenkapital (wird abgezogen, da es Ihre Investition war).',
        totalResult: '<strong>= Gesamt-Ergebnis:</strong> Netto-Cashflow + Immobilienwert − Restschuld − Eigenkapital = Ihr Vermögenszuwachs (inkl. Eigenkapital).'
      }
    }
  };

  // Field-specific warnings (separate from help texts)
  fieldWarnings = {
    remainingDebtAfterBindingTerm: {
      title: '⚠️ Restschuld-Problem',
      content: '<strong>Warnung: Negative Restschuld!</strong><br><br>' +
               'Die verbleibende Schuld nach Ende der Zinsbindung ist negativ. ' +
               'Dies ist mathematisch unmöglich und weist auf ein Problem hin:<br><br>' +
               '• <strong>Tilgungsrate zu hoch:</strong> Mit der aktuellen Tilgung tilgen Sie zu scharf<br>' +
               '• <strong>Sondertilgungen zu hoch:</strong> Reduzieren Sie die zusätzlichen Zahlungen<br>' +
               '• <strong>Bindungsfrist zu kurz:</strong> Eine längere Bindung könnte helfen<br>' +
               '• <strong>Eingabedaten prüfen:</strong> Sind alle Werte korrekt?<br><br>' +
               '<strong>Lösung:</strong> Passen Sie die Finanzierungsparameter an.<br><br>' +
               '<span style="color: #22c55e;"><strong>💡 Hinweis:</strong> Bei minimalen negativen Beträgen (wenige Euro) können Sie diese Warnung ignorieren. Dies ist meist ein Rundungsproblem bei der Berechnung.</span>'
    },
    changedInterestRateWarning: {
      title: '⚠️ Keine Anschlusfinanzierung',
      content: '<strong>Warnung: Feldwert nicht verfügbar!</strong><br><br>' +
               'Der Kredit wird mit der Erstfinanzierung vollständig abbezahlt. ' +
               'Eine Anschlusfinanzierung mit neuem Zinssatz ist daher nicht möglich.<br><br>' +
               'Dieses Feld ist nur aktiviert, wenn eine Restschuld nach der Zinsbindungszeit der Erstfinanzierung übrig bleibt.'
    },
    scenariosWarning: {
      title: '⚠️ Szenarien nicht berechnet',
      content: '<strong>Warnung: Keine Restschuld vorhanden!</strong><br><br>' +
               'Der Kredit wird mit der Erstfinanzierung (Variante 1) vollständig abbezahlt. ' +
               'Es bleibt keine Restschuld nach der Zinsbindungsperiode übrig.<br><br>' +
               'Daher sind:<br>' +
               '• <strong>Variante 2</strong> (Komplett-Tilgung bei gleichbleibendem Zinssatz) nicht notwendig<br>' +
               '• <strong>Variante 3</strong> (Komplett-Tilgung mit verändertem Zinssatz) nicht möglich<br><br>' +
               'Diese Szenarien werden daher nicht berechnet.'
    },
    creditTermExceedsLimit: {
      type: 'warning',
      title: '⚠️ Gesamtlaufzeit überschreitet 100 Jahre',
      content: '<strong>Warnung: Gesamtlaufzeit überschreitet 100 Jahre!</strong><br><br>' +
               'Die Berechnung wurde bei 1200 Monaten (100 Jahren) für die Anschlussfinanzierung gestoppt.<br><br>' +
               '<strong style="color: #ef4444;">Das Szenario ist in dieser Form nicht realistisch.</strong><br><br>' +
               '<strong>Zu korrigierende Felder:</strong><br><br>' +
               '<strong>1. Monatliche Rate erhöhen</strong><br>' +
               '   → Eine höhere Rate führt zu kürzerer Laufzeit<br><br>' +
               '<strong>2. Jährliche Sondertilgung hinzufügen/erhöhen</strong><br>' +
               '   → Zusätzliche Zahlungen beschleunigen die Tilgung<br><br>' +
               '<span style="color: #22c55e;"><strong>💡 Hinweis:</strong> ' +
               'Typische Gesamtlaufzeiten bei Immobilienfinanzierungen liegen zwischen 15-40 Jahren.</span>'
    },
    monthlyRateNegativeAmortization: {
      type: 'warning',
      title: '⚠️ Kreditrate zu niedrig',
      content: '<strong>Warnung: Kreditrate zu niedrig!</strong><br><br>' +
               'Die eingegebene Kreditrate ist so niedrig, dass die daraus berechnete Anfängliche Tilgung negativ würde.<br><br>' +
               '<strong style="color: #ef4444;">Das ist nicht möglich - die Tilgung muss mindestens 0% sein.</strong><br><br>' +
               '<strong>Lösung:</strong><br><br>' +
               'Erhöhen Sie die <strong>monatliche Kreditrate</strong>, damit die Tilgung wieder positiv wird.<br><br>' +
               '<span style="color: #22c55e;"><strong>💡 Hinweis:</strong> ' +
               'Die Rate muss höher sein als die monatlich anfallenden Zinsen.</span>'
    },
    scenariosWarning_Scenario3Over100Years: {
      type: 'warning',
      title: '⚠️ Variante 3 kann nicht berechnet werden',
      content: '<strong>Warnung: Variante 3 überschreitet eine Kreditlaufzeit von 100 Jahren!</strong><br><br>' +
               'Die Berechnung wurde bei 1200 Monaten (100 Jahren) gestoppt.<br><br>' +
               '<strong style="color: #ef4444;">Das Szenario ist in dieser Form nicht realistisch.</strong><br><br>' +
               '<strong>Zu korrigierende Felder:</strong><br><br>' +
               '<strong>1. Monatliche Rate erhöhen</strong><br>' +
               '   → Eine höhere Rate führt zu kürzerer Laufzeit<br><br>' +
               '<strong>2. Jährliche Sondertilgung hinzufügen/erhöhen</strong><br>' +
               '   → Zusätzliche Zahlungen beschleunigen die Tilgung<br><br>' +
               '<span style="color: #22c55e;"><strong>💡 Hinweis:</strong> ' +
               'Typische Gesamtlaufzeiten bei Immobilienfinanzierungen liegen zwischen 15-40 Jahren.</span>'
    },
    scenariosWarning_Scenario2Over100Years: {
      type: 'warning',
      title: '⚠️ Variante 2 und 3 können nicht berechnet werden',
      content: '<strong>Warnung: Varianten 2 und 3 überschreiten eine Kreditlaufzeit von 100 Jahren!</strong><br><br>' +
               'Die Berechnung wurde bei 1200 Monaten (100 Jahren) gestoppt.<br><br>' +
               '<strong style="color: #ef4444;">Das Szenario ist in dieser Form nicht realistisch.</strong><br><br>' +
               '<strong>Zu korrigierende Felder:</strong><br><br>' +
               '<strong>1. Monatliche Rate erhöhen</strong><br>' +
               '   → Eine höhere Rate führt zu kürzerer Laufzeit<br><br>' +
               '<strong>2. Jährliche Sondertilgung hinzufügen/erhöhen</strong><br>' +
               '   → Zusätzliche Zahlungen beschleunigen die Tilgung<br><br>' +
               '<span style="color: #22c55e;"><strong>💡 Hinweis:</strong> ' +
               'Typische Gesamtlaufzeiten bei Immobilienfinanzierungen liegen zwischen 15-40 Jahren.</span>'
    }
  };

  // Tooltip state for mobile
  activeTooltip = signal<string | null>(null);
  
  // Help sidebar visibility
  helpSidebarVisible = computed(() => this.authService.helpSidebarVisible());
  mobileMenuOpen = signal<boolean>(false);
  
  // Dialog component references
  saveDialog = viewChild(SaveCalculationDialogComponent);
  loadDialog = viewChild(LoadCalculationDialogComponent);
  renameDialog = viewChild(RenameCalculationDialogComponent);
  deleteDialog = viewChild(DeleteConfirmationDialogComponent);
  duplicateDialog = viewChild(DuplicateCalculationDialogComponent);
  exportCalculationDialog = viewChild(ExportCalculationDialogComponent);
  exportComparisonDialog = viewChild(ExportComparisonDialogComponent);
  
  // Current calculation state
  currentCalculationId = signal<number | null>(null);
  currentCalculationName = signal<string>('');
  
  // Calculation name dropdown menu
  nameDropdownOpen = signal<boolean>(false);
  
  // Action menu
  actionMenuOpen = signal<boolean>(false);
  
  // Dirty state tracking
  private lastSavedCalculationData = signal<CalculationData | null>(null);
  isCalculationModified = computed(() => {
    const current = this.getCurrentCalculationData();
    const saved = this.lastSavedCalculationData();
    
    // If no calculation loaded and no inputs → not modified
    if (!this.currentCalculationId() && !this.hasAnyInput(current)) {
      return false;
    }
    
    // If no calculation loaded but has inputs → modified (new calculation)
    if (!this.currentCalculationId() && this.hasAnyInput(current)) {
      return true;
    }
    
    // If calculation loaded and changed → modified
    if (this.currentCalculationId() && saved) {
      return JSON.stringify(current) !== JSON.stringify(saved);
    }
    
    return false;
  });
  
  // Prevent mutual update loops
  private isUpdatingAgentCosts = false;
  private isUpdatingNotaryCosts = false;
  private isUpdatingLandRegistryCosts = false;
  private isUpdatingAcquisitionTax = false;

  // Address signals
  country = signal('Deutschland');
  city = signal('');
  address = signal('');
  postalCode = signal('');

  // Form input signals
  purchasePriceInput = signal('');
  agentCostsInput = signal('');
  agentCostsPercentageInput = signal('');
  acquisitionTaxInput = signal('');
  acquisitionTaxPercentageInput = signal('');
  notaryCostsInput = signal('');
  notaryCostsPercentageInput = signal('');
  landRegistryCostsInput = signal('');
  landRegistryCostsPercentageInput = signal('');
  otherCostsInput = signal('');
  propertyTaxRateInput = signal('');
  propertyTaxInput = signal('');
  eigenkapitalInput = signal('');
  
  // Financing signals
  creditTermInput = signal('');
  amortizationRateInput = signal('');
  interestRateInput = signal('');
  interestBindingTermInput = signal('');
  annualExtraRepaymentInput = signal('');
  monthlyRateInput = signal('');
  
  // Changed interest rate for second simulation
  changedInterestRateInput = signal('');
  
  // Track which financing field was last changed (amortizationRate or monthlyRate)
  lastFinancingFieldChanged = signal<'amortizationRate' | 'monthlyRate' | null>(null);
  
  // Running expenses signals
  adminCostsInput = signal('');
  taxCostsInput = signal('');
  houseMoneyInput = signal('');
  insuranceInput = signal('');
  repairCostsInput = signal('');
  otherExpensesInput = signal('');
  
  // Rental price signals
  objectSizeInput = signal('');
  constructionYearInput = signal('');
  rentalPriceInput = signal('');
  monthlyRentalPriceInput = signal('');
  yearlyRentalPriceInput = signal('');
  additionalMonthlyIncomeInput = signal('');
  additionalYearlyIncomeInput = signal('');

  // Track last city to detect actual changes
  private lastCity = signal('');
  // Track last purchase price to detect changes
  private lastPurchasePrice = signal(0);
  // Track if we've already initialized purchase price tracking
  private purchasePriceInitialized = signal(false);

  // Toast notification
  toastMessage = signal<{ text: string; type: 'success' | 'error' | 'ai' | 'warning' } | null>(null);
  private toastTimeoutId: any = null;  // Speichere Timeout-ID für Cleanup

  // Section collapse/expand states (only address data initially expanded)
  addressDataExpanded = signal(true);
  purchaseCostsExpanded = signal(false);
  objectIncomeExpanded = signal(false);
  financingExpanded = signal(true);
  runningExpensesExpanded = signal(false);
  amortisationExpanded = signal(false);

  // Track if sections were manually opened by user (controls auto-expand)
  // FALSE = auto-expand is allowed, TRUE = auto-expand is blocked
  purchaseCostsUserOpened = signal(false);
  objectIncomeUserOpened = signal(false);
  financingUserOpened = signal(false);
  runningExpensesUserOpened = signal(false);
  amortisationUserOpened = signal(false);
  scenariosUserOpened = signal(false);

  // Calculator signals
  number1Display = signal('');
  number2Display = signal('');
  resultDisplay = signal('');

  // Loading and UI states
  isLoadingCountries = signal(false);
  isLoadingCities = signal(false);
  isLoadingAddresses = signal(false);
  isLoadingRentalPrice = signal(false);
  isLoading = signal(false);
  error = signal('');
  rentalPriceError = signal('');

  isLoadingAi = signal(false);
  isLoadingAiAppreciationAllScenarios = signal(false);
  aiError = signal('');
  
  // Track if acquisition tax percentage was manually changed
  acquisitionTaxPercentageManuallyChanged = signal(false);
  // Track if acquisition tax € was manually changed
  acquisitionTaxManuallyChanged = signal(false);
  // Track if agent costs € was manually changed
  agentCostsManuallyChanged = signal(false);

  // Track if initial values have been set (to avoid overwriting user input)
  private hasInitializedAgentCosts = false;
  private hasInitializedNotaryCosts = false;
  private hasInitializedLandRegistryCosts = false;

  // Track which rental price field is currently focused
  rentalPriceFocused = signal(false);
  monthlyRentalPriceFocused = signal(false);
  yearlyRentalPriceFocused = signal(false);
  additionalMonthlyIncomeFocused = signal(false);
  additionalYearlyIncomeFocused = signal(false);
  
  // Track if we just auto-calculated rental prices
  justAutoCalculatedRentalPrices = signal(false);

  showCountrySuggestions = signal(false);
  showCitySuggestions = signal(false);
  showAddressSuggestions = signal(false);

  countrySuggestions = signal<NominatimResult[]>([]);
  citySuggestions = signal<NominatimResult[]>([]);
  addressSuggestions = signal<NominatimResult[]>([]);

  // State selection - automatically derived from city
  federalState = computed(() => {
    const cityValue = this.city();
    if (cityValue) {
      return this.taxService.getStateByCity(cityValue) ?? '';
    }
    return '';
  });

  availableStates = computed(() => this.taxService.getStateNames());

  // Autocomplete delay timer
  private countryDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private cityDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private addressDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  // Computed signals for parsed values
  private purchasePrice = computed(() => this.parseDecimalInput(this.purchasePriceInput()));
  private agentCosts = computed(() => this.parseDecimalInput(this.agentCostsInput()));
  private agentCostsPercentage = computed(() => this.parseDecimalInput(this.agentCostsPercentageInput()));
  private acquisitionTax = computed(() => this.parseDecimalInput(this.acquisitionTaxInput()));
  private acquisitionTaxPercentage = computed(() => this.parseDecimalInput(this.acquisitionTaxPercentageInput()));
  private notaryCosts = computed(() => this.parseDecimalInput(this.notaryCostsInput()));
  private notaryCostsPercentage = computed(() => this.parseDecimalInput(this.notaryCostsPercentageInput()));
  private landRegistryCosts = computed(() => this.parseDecimalInput(this.landRegistryCostsInput()));
  private landRegistryCostsPercentage = computed(() => this.parseDecimalInput(this.landRegistryCostsPercentageInput()));
  private otherCosts = computed(() => this.parseDecimalInput(this.otherCostsInput()));
  private propertyTaxRate = computed(() => this.parseDecimalInput(this.propertyTaxRateInput()));
  private propertyTax = computed(() => this.parseDecimalInput(this.propertyTaxInput()));

  private objectSize = computed(() => this.parseDecimalInput(this.objectSizeInput()));
  /** Baujahr als number oder null (leer/unbekannt). Validiert im Bereich 1800..aktuelles Jahr. */
  private constructionYearNumber = computed<number | null>(() => {
    const raw = this.constructionYearInput().trim();
    if (!raw) return null;
    const n = parseInt(raw, 10);
    if (isNaN(n)) return null;
    const currentYear = new Date().getFullYear();
    if (n < 1800 || n > currentYear) return null;
    return n;
  });
  private rentalPrice = computed(() => this.parseDecimalInput(this.rentalPriceInput()));
  private monthlyRentalPrice = computed(() => this.parseDecimalInput(this.monthlyRentalPriceInput()));
  private yearlyRentalPrice = computed(() => this.parseDecimalInput(this.yearlyRentalPriceInput()));
  private additionalMonthlyIncome = computed(() => this.parseDecimalInput(this.additionalMonthlyIncomeInput()));
  private additionalYearlyIncome = computed(() => this.parseDecimalInput(this.additionalYearlyIncomeInput()));
  
  // Running expenses parsed values
  private adminCosts = computed(() => this.parseDecimalInput(this.adminCostsInput()));
  private taxCosts = computed(() => this.parseDecimalInput(this.taxCostsInput()));
  private houseMoney = computed(() => this.parseDecimalInput(this.houseMoneyInput()));
  private insurance = computed(() => this.parseDecimalInput(this.insuranceInput()));
  private repairCosts = computed(() => this.parseDecimalInput(this.repairCostsInput()));
  private otherExpenses = computed(() => this.parseDecimalInput(this.otherExpensesInput()));

  private creditTerm = computed(() => this.parseDecimalInput(this.creditTermInput()));
  private amortizationRate = computed(() => this.parseDecimalInput(this.amortizationRateInput()));
  private interestRate = computed(() => this.parseDecimalInput(this.interestRateInput()));
  private interestBindingTerm = computed(() => this.parseDecimalInput(this.interestBindingTermInput()));
  private annualExtraRepayment = computed(() => this.parseDecimalInput(this.annualExtraRepaymentInput()));

  private number1 = computed(() => {
    const val = this.number1Display();
    return val === '' ? null : Number(val);
  });

  private number2 = computed(() => {
    const val = this.number2Display();
    return val === '' ? null : Number(val);
  });

  // Computed calculations
  totalMonthlyIncomeDisplay = computed(() => {
    const monthly = this.monthlyRentalPrice() ?? 0;
    const additional = this.additionalMonthlyIncome() ?? 0;
    const total = monthly + additional;
    return this.formatCurrency(total);
  });

  totalYearlyIncomeDisplay = computed(() => {
    const yearly = this.yearlyRentalPrice() ?? 0;
    const additional = this.additionalYearlyIncome() ?? 0;
    const total = yearly + additional;
    return this.formatCurrency(total);
  });

  postalCodeDisplay = computed(() => this.postalCode());

  totalBuyingCostsDisplay = computed(() => {
    const purchasePrice = this.purchasePrice() ?? 0;
    const agentCosts = this.agentCosts() ?? 0;
    const acquisitionTax = this.acquisitionTax() ?? 0;
    const notaryCosts = this.notaryCosts() ?? 0;
    const landRegistryCosts = this.landRegistryCosts() ?? 0;
    const otherCosts = this.otherCosts() ?? 0;
    
    const total = purchasePrice + agentCosts + acquisitionTax + notaryCosts + landRegistryCosts + otherCosts;
    return this.formatCurrency(total);
  });

  private eigenkapital = computed(() => this.parseDecimalInput(this.eigenkapitalInput()));

  kreditbedarfDisplay = computed(() => {
    const purchasePrice = this.purchasePrice() ?? 0;
    const agentCosts = this.agentCosts() ?? 0;
    const acquisitionTax = this.acquisitionTax() ?? 0;
    const notaryCosts = this.notaryCosts() ?? 0;
    const landRegistryCosts = this.landRegistryCosts() ?? 0;
    const otherCosts = this.otherCosts() ?? 0;
    const gesamtKaufkosten = purchasePrice + agentCosts + acquisitionTax + notaryCosts + landRegistryCosts + otherCosts;
    const eigenkapital = this.eigenkapital() ?? 0;
    const kreditbedarf = gesamtKaufkosten - eigenkapital;
    return this.formatCurrency(kreditbedarf);
  });

  kaufpreisProQuadratmeterDisplay = computed(() => {
    const purchasePrice = this.purchasePrice() ?? 0;
    const objectSize = this.objectSize() ?? 0;
    if (objectSize === 0) {
      return '';
    }
    const kaufpreisProQm = purchasePrice / objectSize;
    return this.formatCurrency(kaufpreisProQm);
  });

  xfachVonKaufpreisDisplay = computed(() => {
    const purchasePrice = this.purchasePrice() ?? 0;
    const totalYearlyIncome = (this.monthlyRentalPrice() ?? 0) * 12 + (this.additionalYearlyIncome() ?? 0);
    if (totalYearlyIncome === 0) {
      return '';
    }
    const xfach = purchasePrice / totalYearlyIncome;
    return this.formatDecimal(xfach);
  });

  xfachVonGesamtKaufkostenDisplay = computed(() => {
    const purchasePrice = this.purchasePrice() ?? 0;
    const agentCosts = this.agentCosts() ?? 0;
    const acquisitionTax = this.acquisitionTax() ?? 0;
    const notaryCosts = this.notaryCosts() ?? 0;
    const landRegistryCosts = this.landRegistryCosts() ?? 0;
    const otherCosts = this.otherCosts() ?? 0;
    const gesamtKaufkosten = purchasePrice + agentCosts + acquisitionTax + notaryCosts + landRegistryCosts + otherCosts;
    
    const yearly = this.yearlyRentalPrice() ?? 0;
    const additionalYearly = this.additionalYearlyIncome() ?? 0;
    const totalYearlyIncome = yearly + additionalYearly;
    
    if (totalYearlyIncome === 0) {
      return '';
    }
    const xfach = gesamtKaufkosten / totalYearlyIncome;
    return this.formatDecimal(xfach);
  });

  // Financing calculations
  amortizationAmountDisplay = computed(() => {
    const kreditbedarf = (this.parseDecimalInput(this.kreditbedarfDisplay()) ?? 0);
    const amortizationRate = this.amortizationRate() ?? 0;
    if (kreditbedarf === 0 || amortizationRate === 0) {
      return '';
    }
    const amortizationAmount = (kreditbedarf * amortizationRate) / 100;
    return this.formatCurrency(amortizationAmount);
  });

  interestAmountDisplay = computed(() => {
    const kreditbedarf = (this.parseDecimalInput(this.kreditbedarfDisplay()) ?? 0);
    const interestRate = this.interestRate() ?? 0;
    if (kreditbedarf === 0 || interestRate === 0) {
      return '';
    }
    const interestAmount = (kreditbedarf * interestRate) / 100;
    return this.formatCurrency(interestAmount);
  });

  monthlyRateDisplay = computed(() => {
    // Wenn user die Kreditrate manuell eingegeben hat, diese anzeigen
    if (this.lastFinancingFieldChanged() === 'monthlyRate' && this.monthlyRateInput()) {
      return this.monthlyRateInput();
    }
    
    const amortizationAmount = (this.parseDecimalInput(this.amortizationAmountDisplay()) ?? 0);
    const interestAmount = (this.parseDecimalInput(this.interestAmountDisplay()) ?? 0);
    
    if (amortizationAmount === 0 && interestAmount === 0) {
      return '';
    }
    
    // Monatliche Rate = (jährliche Tilgung + jährliche Zinsen) / 12
    const monthlyRate = (amortizationAmount + interestAmount) / 12;
    return this.formatCurrency(monthlyRate);
  });

  amortizationRateDisplay = computed(() => {
    // Wenn user die Kreditrate manuell eingegeben hat, berechne die Anfängliche Tilgung
    if (this.lastFinancingFieldChanged() === 'monthlyRate' && this.monthlyRateInput()) {
      const kreditbedarf = this.parseDecimalInput(this.kreditbedarfDisplay()) ?? 0;
      const monthlyRate = this.parseDecimalInput(this.monthlyRateInput()) ?? 0;
      const interestRate = this.parseDecimalInput(this.interestRateInput()) ?? 0;
      
      if (kreditbedarf === 0 || interestRate === 0 || monthlyRate === 0) {
        return this.amortizationRateInput();
      }
      
      // Berechne jährliche Zinsen: Kreditbedarf * Zinssatz / 100
      const yearlyInterest = (kreditbedarf * interestRate) / 100;
      
      // Monatliche Rate = (jährliche Tilgung + jährliche Zinsen) / 12
      // Also: jährliche Tilgung = (monthlyRate * 12) - jährliche Zinsen
      const yearlyAmortization = (monthlyRate * 12) - yearlyInterest;
      
      // Anfängliche Tilgung % = (jährliche Tilgung / Kreditbedarf) * 100
      const amortizationRate = (yearlyAmortization / kreditbedarf) * 100;
      
      return this.formatDecimal(amortizationRate);
    }
    
    // Ansonsten zeige die manuell eingegebene Anfängliche Tilgung
    return this.amortizationRateInput();
  });

  monthlyRateNegativeAmortization = computed(() => {
    const kreditbedarf = this.parseDecimalInput(this.kreditbedarfDisplay()) ?? 0;
    const monthlyRate = this.parseDecimalInput(this.monthlyRateInput()) ?? 0;
    const interestRate = this.parseDecimalInput(this.interestRateInput()) ?? 0;
    
    if (kreditbedarf === 0 || interestRate === 0 || monthlyRate === 0) {
      return false;
    }
    
    // Berechne jährliche Zinsen
    const yearlyInterest = (kreditbedarf * interestRate) / 100;
    
    // Berechne jährliche Tilgung = (monthlyRate * 12) - jährliche Zinsen
    const yearlyAmortization = (monthlyRate * 12) - yearlyInterest;
    
    // Prüfe ob Tilgung negativ
    return yearlyAmortization < 0;
  });

  monthlyAmortizationDisplay = computed(() => {
    const amortizationAmount = (this.parseDecimalInput(this.amortizationAmountDisplay()) ?? 0);
    if (amortizationAmount === 0) {
      return '';
    }
    const monthlyAmortization = amortizationAmount / 12;
    return this.formatCurrency(monthlyAmortization);
  });

  monthlyInterestDisplay = computed(() => {
    const interestAmount = (this.parseDecimalInput(this.interestAmountDisplay()) ?? 0);
    if (interestAmount === 0) {
      return '';
    }
    const monthlyInterest = interestAmount / 12;
    return this.formatCurrency(monthlyInterest);
  });

  totalAmortizationOverLifetime = computed(() => {
    const monthlyRate = (this.parseDecimalInput(this.monthlyRateDisplay()) ?? 0);
    const interestBindingTerm = this.parseDecimalInput(this.interestBindingTermInput()) ?? 0;
    const annualExtraRepayment = this.annualExtraRepayment() ?? 0;
    const totalInterest = (this.parseDecimalInput(this.totalInterestOverLifetime()) ?? 0);
    
    if (monthlyRate === 0 || interestBindingTerm === 0) {
      return '';
    }
    
    // Gesamttilgung = Gesamtzahlungen - Gesamtzinsen
    // Gesamtzahlungen = (monatliche Rate × Anzahl Monate) + (Sondertilgung × Anzahl VOLLER Jahre)
    // Sondertilgung wird nur für volle Jahre berechnet (z.B. bei 10,5 Jahren = 10 Sondertilgungen)
    const numberOfMonths = interestBindingTerm * 12;
    const numberOfFullYears = Math.floor(interestBindingTerm);
    const totalPayments = (monthlyRate * numberOfMonths) + (annualExtraRepayment * numberOfFullYears);
    const totalAmortization = totalPayments - totalInterest;
    
    return this.formatCurrency(totalAmortization);
  });

  totalInterestOverLifetime = computed(() => {
    const monthlyRate = (this.parseDecimalInput(this.monthlyRateDisplay()) ?? 0);
    const interestBindingTerm = this.parseDecimalInput(this.interestBindingTermInput()) ?? 0;
    const kreditbedarf = (this.parseDecimalInput(this.kreditbedarfDisplay()) ?? 0);
    const annualExtraRepayment = this.annualExtraRepayment() ?? 0;
    
    if (monthlyRate === 0 || interestBindingTerm === 0 || kreditbedarf === 0) {
      return '';
    }
    
    // Berechne Gesamtzinsen über die Zinsbindungsperiode (nicht die Gesamtlaufzeit!)
    // Sondertilgung wird am Jahresende (Monat 12, 24, 36, etc.) gezahlt
    const interestRate = this.interestRate() ?? 0;
    const monthlyInterestRate = interestRate / 12 / 100;
    const numberOfMonths = interestBindingTerm * 12;
    
    let remainingDebt = kreditbedarf;
    let totalInterest = 0;
    
    // Amortisiere Schuld monatlich und berechne Gesamtzinsen
    for (let month = 0; month < numberOfMonths; month++) {
      const interest = remainingDebt * monthlyInterestRate;
      totalInterest += interest;
      const principalPayment = monthlyRate - interest;
      
      // Sondertilgung wird im 12., 24., 36., etc. Monat gezahlt
      let extraRepaymentThisMonth = 0;
      if ((month + 1) % 12 === 0 && annualExtraRepayment > 0) {
        extraRepaymentThisMonth = annualExtraRepayment;
      }
      
      remainingDebt -= (principalPayment + extraRepaymentThisMonth);
      
      if (remainingDebt <= 0) {
        break;
      }
    }
    
    return this.formatCurrency(totalInterest);
  });

  calculatedCreditTermDisplay = computed(() => {
    const kreditbedarf = (this.parseDecimalInput(this.kreditbedarfDisplay()) ?? 0);
    const interestRate = this.interestRate() ?? 0;
    const monthlyRate = (this.parseDecimalInput(this.monthlyRateDisplay()) ?? 0);
    const annualExtraRepayment = this.annualExtraRepayment() ?? 0;
    
    if (kreditbedarf === 0 || interestRate === 0 || monthlyRate === 0) {
      return '';
    }
    
    // Berechne Laufzeit durch Amortisationssimulation
    // Sondertilgung wird am Jahresende (Monat 12, 24, 36, etc.) gezahlt
    const monthlyInterestRate = interestRate / 12 / 100;
    let remainingDebt = kreditbedarf;
    let month = 0;
    
    while (remainingDebt > 0 && month < this.MAX_AMORTIZATION_MONTHS) {
      const interest = remainingDebt * monthlyInterestRate;
      const principalPayment = monthlyRate - interest;
      
      // Sondertilgung wird im 12., 24., 36., etc. Monat gezahlt
      let extraRepaymentThisMonth = 0;
      if ((month + 1) % 12 === 0 && annualExtraRepayment > 0) {
        extraRepaymentThisMonth = annualExtraRepayment;
      }
      
      remainingDebt -= (principalPayment + extraRepaymentThisMonth);
      month++;
      
      // Stoppe bei negativer Restschuld
      if (remainingDebt <= 0) {
        break;
      }
    }
    
    // Return empty if loop hit the MAX_AMORTIZATION_MONTHS limit (meaning term > 100 years)
    if (month >= this.MAX_AMORTIZATION_MONTHS && remainingDebt > 0) {
      return '';
    }
    
    const creditTermYears = month / 12;
    
    // Format to 1 decimal place
    return this.formatDecimal(creditTermYears, 1);
  });

  creditTermConstantRatesExceedsLimit = computed(() => {
    const kreditbedarf = (this.parseDecimalInput(this.kreditbedarfDisplay()) ?? 0);
    const interestRate = this.interestRate() ?? 0;
    const monthlyRate = (this.parseDecimalInput(this.monthlyRateDisplay()) ?? 0);
    const annualExtraRepayment = this.annualExtraRepayment() ?? 0;
    
    if (kreditbedarf === 0 || interestRate === 0 || monthlyRate === 0) {
      return false;
    }
    
    // Berechne Laufzeit durch Amortisationssimulation
    const monthlyInterestRate = interestRate / 12 / 100;
    let remainingDebt = kreditbedarf;
    let month = 0;
    
    while (remainingDebt > 0 && month < this.MAX_AMORTIZATION_MONTHS) {
      const interest = remainingDebt * monthlyInterestRate;
      const principalPayment = monthlyRate - interest;
      
      // Sondertilgung wird im 12., 24., 36., etc. Monat gezahlt
      let extraRepaymentThisMonth = 0;
      if ((month + 1) % 12 === 0 && annualExtraRepayment > 0) {
        extraRepaymentThisMonth = annualExtraRepayment;
      }
      
      remainingDebt -= (principalPayment + extraRepaymentThisMonth);
      month++;
      
      if (remainingDebt <= 0) {
        break;
      }
    }
    
    const creditTermYears = month / 12;
    
    // Return true if loop hit the MAX_AMORTIZATION_MONTHS limit (meaning term > 100 years)
    return month >= this.MAX_AMORTIZATION_MONTHS && remainingDebt > 0;
  });

  totalInterestDuringBindingTerm = computed(() => {
    const kreditbedarf = (this.parseDecimalInput(this.kreditbedarfDisplay()) ?? 0);
    const interestRate = this.interestRate() ?? 0;
    const monthlyRate = (this.parseDecimalInput(this.monthlyRateDisplay()) ?? 0);
    const interestBindingTerm = this.interestBindingTerm() ?? 0;
    const annualExtraRepayment = this.annualExtraRepayment() ?? 0;
    
    if (kreditbedarf === 0 || interestRate === 0 || monthlyRate === 0 || interestBindingTerm === 0) {
      return '';
    }
    
    // Berechne Gesamtzinsen NUR während der Zinsbindungsperiode
    // Sondertilgung wird am Jahresende (Monat 12, 24, 36, etc.) gezahlt
    const monthlyInterestRate = interestRate / 12 / 100;
    const numberOfMonths = interestBindingTerm * 12;
    
    let remainingDebt = kreditbedarf;
    let totalInterest = 0;
    
    // Amortisiere Schuld monatlich für die Zinsbindungsperiode und berechne Gesamtzinsen
    for (let month = 0; month < numberOfMonths; month++) {
      const interest = remainingDebt * monthlyInterestRate;
      totalInterest += interest;
      const principalPayment = monthlyRate - interest;
      
      // Sondertilgung wird im 12., 24., 36., etc. Monat gezahlt
      let extraRepaymentThisMonth = 0;
      if ((month + 1) % 12 === 0 && annualExtraRepayment > 0) {
        extraRepaymentThisMonth = annualExtraRepayment;
      }
      
      remainingDebt -= (principalPayment + extraRepaymentThisMonth);
      
      if (remainingDebt <= 0) {
        break;
      }
    }
    
    return this.formatCurrency(totalInterest);
  });

  totalPaymentOverLifetime = computed(() => {
    const totalAmortization = (this.parseDecimalInput(this.totalAmortizationOverLifetime()) ?? 0);
    const totalInterest = (this.parseDecimalInput(this.totalInterestDuringBindingTerm()) ?? 0);
    
    if (totalAmortization === 0 || totalInterest === 0) {
      return '';
    }
    
    const totalPayment = totalAmortization + totalInterest;
    return this.formatCurrency(totalPayment);
  });

  totalCreditCostIncludingRemainingDebt = computed(() => {
    const totalPayment = (this.parseDecimalInput(this.totalPaymentOverLifetime()) ?? 0);
    const remainingDebt = (this.parseDecimalInput(this.remainingDebtAfterBindingTerm()) ?? 0);
    
    if (totalPayment === 0) {
      return '';
    }
    
    const totalCreditCost = totalPayment + remainingDebt;
    return this.formatCurrency(totalCreditCost);
  });

  totalInterestConstantRates = computed(() => {
    const kreditbedarf = (this.parseDecimalInput(this.kreditbedarfDisplay()) ?? 0);
    const interestRate = this.interestRate() ?? 0;
    const monthlyRate = (this.parseDecimalInput(this.monthlyRateDisplay()) ?? 0);
    const annualExtraRepayment = this.annualExtraRepayment() ?? 0;
    
    if (kreditbedarf === 0 || interestRate === 0 || monthlyRate === 0) {
      return '';
    }
    
    // Berechne Gesamtzinsen über die komplette Laufzeit (simulierte Gesamtlaufzeit bei konstanten Zinsen)
    // Sondertilgung wird am Jahresende (Monat 12, 24, 36, etc.) gezahlt
    const monthlyInterestRate = interestRate / 12 / 100;
    let remainingDebt = kreditbedarf;
    let totalInterest = 0;
    let month = 0;
    
    // Amortisiere Schuld monatlich über die komplette Laufzeit
    while (remainingDebt > 0 && month < this.MAX_AMORTIZATION_MONTHS) { // Max 100 Jahre
      const interest = remainingDebt * monthlyInterestRate;
      totalInterest += interest;
      const principalPayment = monthlyRate - interest;
      
      // Sondertilgung wird im 12., 24., 36., etc. Monat gezahlt
      let extraRepaymentThisMonth = 0;
      if ((month + 1) % 12 === 0 && annualExtraRepayment > 0) {
        extraRepaymentThisMonth = annualExtraRepayment;
      }
      
      remainingDebt -= (principalPayment + extraRepaymentThisMonth);
      month++;
      
      // Stoppe Schleife wenn Schuld abbezahlt ist (auch bei negativen Werten)
      // Negative Restschuld bedeutet Überzahlung - keine weiteren Zinsen
      if (remainingDebt <= 0) {
        break;
      }
    }
    
    // Return empty if loop hit the MAX_AMORTIZATION_MONTHS limit (meaning term > 100 years)
    if (month >= this.MAX_AMORTIZATION_MONTHS && remainingDebt > 0) {
      return '';
    }
    
    return this.formatCurrency(totalInterest);
  });

  totalPaymentOverLifetimeConstantRates = computed(() => {
    const kreditbedarf = (this.parseDecimalInput(this.kreditbedarfDisplay()) ?? 0);
    const interestRate = this.interestRate() ?? 0;
    const monthlyRate = (this.parseDecimalInput(this.monthlyRateDisplay()) ?? 0);
    const annualExtraRepayment = this.annualExtraRepayment() ?? 0;
    
    if (kreditbedarf === 0 || interestRate === 0 || monthlyRate === 0) {
      return '';
    }
    
    // Berechne Laufzeit um zu prüfen ob Limit überschritten
    const monthlyInterestRate = interestRate / 12 / 100;
    let remainingDebt = kreditbedarf;
    let month = 0;
    
    while (remainingDebt > 0 && month < this.MAX_AMORTIZATION_MONTHS) {
      const interest = remainingDebt * monthlyInterestRate;
      const principalPayment = monthlyRate - interest;
      
      let extraRepaymentThisMonth = 0;
      if ((month + 1) % 12 === 0 && annualExtraRepayment > 0) {
        extraRepaymentThisMonth = annualExtraRepayment;
      }
      
      remainingDebt -= (principalPayment + extraRepaymentThisMonth);
      month++;
      
      if (remainingDebt <= 0) {
        break;
      }
    }
    
    // Return empty if loop hit the MAX_AMORTIZATION_MONTHS limit (meaning term > 100 years)
    if (month >= this.MAX_AMORTIZATION_MONTHS && remainingDebt > 0) {
      return '';
    }
    
    // Berechne Gesamtzinsen direkt (nicht über Signal)
    remainingDebt = kreditbedarf;
    let totalInterest = 0;
    let monthCounter = 0;
    
    while (remainingDebt > 0 && monthCounter < this.MAX_AMORTIZATION_MONTHS) {
      const interest = remainingDebt * monthlyInterestRate;
      totalInterest += interest;
      const principalPayment = monthlyRate - interest;
      
      let extraRepaymentThisMonth = 0;
      if ((monthCounter + 1) % 12 === 0 && annualExtraRepayment > 0) {
        extraRepaymentThisMonth = annualExtraRepayment;
      }
      
      remainingDebt -= (principalPayment + extraRepaymentThisMonth);
      monthCounter++;
      
      if (remainingDebt <= 0) {
        break;
      }
    }
    
    // Gesamtzahlung = Kreditbedarf (gesamte Tilgung) + Gesamtzinsen
    const totalPayment = kreditbedarf + totalInterest;
    return this.formatCurrency(totalPayment);
  });

  // Calculated interest rate for changed rates scenario (from user input)
  private changedInterestRate = computed(() => this.parseDecimalInput(this.changedInterestRateInput()));

  calculatedCreditTermChangedRates = computed(() => {
    const remainingDebt = (this.parseDecimalInput(this.remainingDebtAfterBindingTerm()) ?? 0);
    const changedInterestRate = this.changedInterestRate() ?? 0;
    const monthlyRate = (this.parseDecimalInput(this.monthlyRateDisplay()) ?? 0);
    const annualExtraRepayment = this.annualExtraRepayment() ?? 0;
    const interestBindingTerm = this.interestBindingTerm() ?? 0;
    
    if (remainingDebt <= 0 || changedInterestRate === 0 || monthlyRate === 0) {
      return '';
    }
    
    // Berechne Laufzeit für die Restschuld mit veränderten Zinsen
    const monthlyInterestRate = changedInterestRate / 12 / 100;
    let remainingDebtForRefinance = remainingDebt;
    let month = 0;
    
    while (remainingDebtForRefinance > 0 && month < this.MAX_AMORTIZATION_MONTHS) {
      const interest = remainingDebtForRefinance * monthlyInterestRate;
      const principalPayment = monthlyRate - interest;
      
      // Sondertilgung wird im 12., 24., 36., etc. Monat gezahlt
      let extraRepaymentThisMonth = 0;
      if ((month + 1) % 12 === 0 && annualExtraRepayment > 0) {
        extraRepaymentThisMonth = annualExtraRepayment;
      }
      
      remainingDebtForRefinance -= (principalPayment + extraRepaymentThisMonth);
      month++;
      
      if (remainingDebtForRefinance <= 0) {
        break;
      }
    }
    
    const refinanceTermYears = month / 12;
    const totalCreditTermYears = interestBindingTerm + refinanceTermYears;
    
    // Return empty if limit exceeded
    if (totalCreditTermYears > 100) {
      return '';
    }
    
    // Format to 1 decimal place
    return this.formatDecimal(totalCreditTermYears, 1);
  });

  creditTermChangedRatesExceedsLimit = computed(() => {
    const remainingDebt = (this.parseDecimalInput(this.remainingDebtAfterBindingTerm()) ?? 0);
    const changedInterestRate = this.changedInterestRate() ?? 0;
    const monthlyRate = (this.parseDecimalInput(this.monthlyRateDisplay()) ?? 0);
    const annualExtraRepayment = this.annualExtraRepayment() ?? 0;
    const interestBindingTerm = this.interestBindingTerm() ?? 0;
    
    if (remainingDebt <= 0 || changedInterestRate === 0 || monthlyRate === 0) {
      return false;
    }
    
    // Berechne Laufzeit für die Restschuld mit veränderten Zinsen
    const monthlyInterestRate = changedInterestRate / 12 / 100;
    let remainingDebtForRefinance = remainingDebt;
    let month = 0;
    
    while (remainingDebtForRefinance > 0 && month < this.MAX_AMORTIZATION_MONTHS) {
      const interest = remainingDebtForRefinance * monthlyInterestRate;
      const principalPayment = monthlyRate - interest;
      
      // Sondertilgung wird im 12., 24., 36., etc. Monat gezahlt
      let extraRepaymentThisMonth = 0;
      if ((month + 1) % 12 === 0 && annualExtraRepayment > 0) {
        extraRepaymentThisMonth = annualExtraRepayment;
      }
      
      remainingDebtForRefinance -= (principalPayment + extraRepaymentThisMonth);
      month++;
      
      if (remainingDebtForRefinance <= 0) {
        break;
      }
    }
    
    const refinanceTermYears = month / 12;
    const totalCreditTermYears = interestBindingTerm + refinanceTermYears;
    
    // Return true if total credit term exceeds 100 years
    return totalCreditTermYears > 100;
  });

  totalInterestChangedRates = computed(() => {
    const remainingDebt = (this.parseDecimalInput(this.remainingDebtAfterBindingTerm()) ?? 0);
    const interestFromFirstPhase = (this.parseDecimalInput(this.totalInterestDuringBindingTerm()) ?? 0);
    const changedInterestRate = this.changedInterestRate() ?? 0;
    const monthlyRate = (this.parseDecimalInput(this.monthlyRateDisplay()) ?? 0);
    const annualExtraRepayment = this.annualExtraRepayment() ?? 0;
    
    if (remainingDebt <= 0 || changedInterestRate === 0 || monthlyRate === 0) {
      return '';
    }
    
    // Berechne Gesamtzinsen für die Restschuld-Refinanzierung mit veränderten Zinsen
    const monthlyInterestRate = changedInterestRate / 12 / 100;
    let remainingDebtForRefinance = remainingDebt;
    let totalInterestForRefinance = 0;
    let month = 0;
    
    while (remainingDebtForRefinance > 0 && month < this.MAX_AMORTIZATION_MONTHS) { // Max 100 Jahre
      const interest = remainingDebtForRefinance * monthlyInterestRate;
      totalInterestForRefinance += interest;
      const principalPayment = monthlyRate - interest;
      
      // Sondertilgung wird im 12., 24., 36., etc. Monat gezahlt
      let extraRepaymentThisMonth = 0;
      if ((month + 1) % 12 === 0 && annualExtraRepayment > 0) {
        extraRepaymentThisMonth = annualExtraRepayment;
      }
      
      remainingDebtForRefinance -= (principalPayment + extraRepaymentThisMonth);
      month++;
      
      // Stoppe bei negativer Restschuld (Guthaben)
      if (remainingDebtForRefinance <= 0) {
        break;
      }
    }
    
    // Check if limit exceeded
    const interestBindingTerm = this.interestBindingTerm() ?? 0;
    const refinanceTermYears = month / 12;
    const totalCreditTermYears = interestBindingTerm + refinanceTermYears;
    if (totalCreditTermYears > 100) {
      return '';
    }
    
    // Gesamtzinsen = Zinsen aus erster Phase + Zinsen für Restschuld
    const totalInterest = interestFromFirstPhase + totalInterestForRefinance;
    return this.formatCurrency(totalInterest);
  });

  totalPaymentOverLifetimeChangedRates = computed(() => {
    const remainingDebt = (this.parseDecimalInput(this.remainingDebtAfterBindingTerm()) ?? 0);
    const totalPaymentFirstPhase = (this.parseDecimalInput(this.totalPaymentOverLifetime()) ?? 0);
    const changedInterestRate = this.changedInterestRate() ?? 0;
    const monthlyRate = (this.parseDecimalInput(this.monthlyRateDisplay()) ?? 0);
    const annualExtraRepayment = this.annualExtraRepayment() ?? 0;
    const interestFromFirstPhase = (this.parseDecimalInput(this.totalInterestDuringBindingTerm()) ?? 0);
    
    if (remainingDebt <= 0 || totalPaymentFirstPhase === 0 || changedInterestRate === 0 || monthlyRate === 0) {
      return '';
    }
    
    // Berechne Gesamtzinsen für die Restschuld-Refinanzierung mit veränderten Zinsen
    const monthlyInterestRate = changedInterestRate / 12 / 100;
    let remainingDebtForRefinance = remainingDebt;
    let totalInterestForRefinance = 0;
    let month = 0;
    
    while (remainingDebtForRefinance > 0 && month < this.MAX_AMORTIZATION_MONTHS) { // Max 100 Jahre
      const interest = remainingDebtForRefinance * monthlyInterestRate;
      totalInterestForRefinance += interest;
      const principalPayment = monthlyRate - interest;
      
      // Sondertilgung wird im 12., 24., 36., etc. Monat gezahlt
      let extraRepaymentThisMonth = 0;
      if ((month + 1) % 12 === 0 && annualExtraRepayment > 0) {
        extraRepaymentThisMonth = annualExtraRepayment;
      }
      
      remainingDebtForRefinance -= (principalPayment + extraRepaymentThisMonth);
      month++;
      
      // Stoppe bei negativer Restschuld (Guthaben)
      if (remainingDebtForRefinance <= 0) {
        break;
      }
    }
    
    // Gesamtzahlung für Restschuld = Restschuld + Zinsen für Restschuld
    const totalPaymentForRefinance = remainingDebt + totalInterestForRefinance;
    
    // Check if limit exceeded
    const interestBindingTerm = this.interestBindingTerm() ?? 0;
    const refinanceTermYears = month / 12;
    const totalCreditTermYears = interestBindingTerm + refinanceTermYears;
    if (totalCreditTermYears > 100) {
      return '';
    }
    
    // Gesamtzahlung = Zahlung erste Phase + Zahlung Refinanzierung
    const totalPayment = totalPaymentFirstPhase + totalPaymentForRefinance;
    return this.formatCurrency(totalPayment);
  });

  remainingDebtAfterBindingTerm = computed(() => {
    const kreditbedarf = (this.parseDecimalInput(this.kreditbedarfDisplay()) ?? 0);
    const interestRate = this.interestRate() ?? 0;
    const monthlyRate = (this.parseDecimalInput(this.monthlyRateDisplay()) ?? 0);
    const interestBindingTerm = this.interestBindingTerm() ?? 0;
    const annualExtraRepayment = this.annualExtraRepayment() ?? 0;
    
    if (kreditbedarf === 0 || interestRate === 0 || monthlyRate === 0 || interestBindingTerm === 0) {
      return '';
    }
    
    // Berechne Restschuld nach Ablauf der Zinsbindung
    // Sondertilgung wird am Jahresende (Monat 12, 24, 36, etc.) gezahlt
    // Restschuld kann auch negativ sein, wenn Sondertilgungen zu hoch sind
    const monthlyInterestRate = interestRate / 12 / 100;
    const numberOfMonths = interestBindingTerm * 12;
    
    let remainingDebt = kreditbedarf;
    
    // Amortisiere Schuld monatlich
    for (let month = 0; month < numberOfMonths; month++) {
      const interest = remainingDebt * monthlyInterestRate;
      const principalPayment = monthlyRate - interest;
      
      // Sondertilgung wird im 12., 24., 36., etc. Monat gezahlt
      let extraRepaymentThisMonth = 0;
      if ((month + 1) % 12 === 0 && annualExtraRepayment > 0) {
        extraRepaymentThisMonth = annualExtraRepayment;
      }
      
      remainingDebt -= (principalPayment + extraRepaymentThisMonth);
    }
    
    return this.formatCurrency(remainingDebt);
  });

  // Amortisation calculations
  monthlyIncomeDisplay = computed(() => {
    const monthlyRental = this.monthlyRentalPrice() ?? 0;
    const additionalMonthly = this.additionalMonthlyIncome() ?? 0;
    const totalMonthly = monthlyRental + additionalMonthly;
    if (totalMonthly === 0) {
      return '';
    }
    return this.formatCurrency(totalMonthly);
  });

  monthlyFinancingExpensesDisplay = computed(() => {
    const monthlyRate = this.parseDecimalInput(this.monthlyRateDisplay()) ?? 0;
    if (monthlyRate === 0) {
      return '';
    }
    return this.formatCurrency(monthlyRate);
  });

  monthlyOtherExpensesDisplay = computed(() => {
    const adminCosts = this.adminCosts() ?? 0;
    const taxCosts = this.taxCosts() ?? 0;
    const houseMoney = this.houseMoney() ?? 0;
    const insurance = this.insurance() ?? 0;
    const repairCosts = this.repairCosts() ?? 0;
    const otherExpenses = this.otherExpenses() ?? 0;
    const totalYearly = adminCosts + taxCosts + houseMoney + insurance + repairCosts + otherExpenses;
    if (totalYearly === 0) {
      return '';
    }
    const totalMonthly = totalYearly / 12;
    return this.formatCurrency(totalMonthly);
  });

  monthlyIncomeExcessDisplay = computed(() => {
    const monthlyIncome = this.parseDecimalInput(this.monthlyIncomeDisplay()) ?? 0;
    const monthlyFinancing = this.parseDecimalInput(this.monthlyFinancingExpensesDisplay()) ?? 0;
    const monthlyOtherExpenses = this.parseDecimalInput(this.monthlyOtherExpensesDisplay()) ?? 0;
    
    const excess = monthlyIncome - monthlyFinancing - monthlyOtherExpenses;
    if (monthlyIncome === 0) {
      return '';
    }
    return this.formatCurrency(excess);
  });

  yearlyIncomeExcessDisplay = computed(() => {
    const monthlyExcess = this.parseDecimalInput(this.monthlyIncomeExcessDisplay()) ?? 0;
    const yearlyExcess = monthlyExcess * 12;
    if (monthlyExcess === 0) {
      return '';
    }
    return this.formatCurrency(yearlyExcess);
  });

  isMonthlyIncomeExcessNegative = computed(() => {
    const monthlyIncome = this.parseDecimalInput(this.monthlyIncomeDisplay()) ?? 0;
    const monthlyFinancing = this.parseDecimalInput(this.monthlyFinancingExpensesDisplay()) ?? 0;
    const monthlyOtherExpenses = this.parseDecimalInput(this.monthlyOtherExpensesDisplay()) ?? 0;
    const excess = monthlyIncome - monthlyFinancing - monthlyOtherExpenses;
    return excess < 0;
  });

  isYearlyIncomeExcessNegative = computed(() => {
    const monthlyExcess = this.parseDecimalInput(this.monthlyIncomeExcessDisplay()) ?? 0;
    return (monthlyExcess * 12) < 0;
  });

  yearlyFinancingExpensesDisplay = computed(() => {
    const monthlyFinancing = this.parseDecimalInput(this.monthlyFinancingExpensesDisplay()) ?? 0;
    const yearlyFinancing = monthlyFinancing * 12;
    if (monthlyFinancing === 0) {
      return '';
    }
    return this.formatCurrency(yearlyFinancing);
  });

  yearlyOtherExpensesDisplay = computed(() => {
    const monthlyOtherExpenses = this.parseDecimalInput(this.monthlyOtherExpensesDisplay()) ?? 0;
    const yearlyOtherExpenses = monthlyOtherExpenses * 12;
    if (monthlyOtherExpenses === 0) {
      return '';
    }
    return this.formatCurrency(yearlyOtherExpenses);
  });

  monthlyTotalExpensesDisplay = computed(() => {
    const monthlyFinancing = this.parseDecimalInput(this.monthlyFinancingExpensesDisplay()) ?? 0;
    const monthlyOtherExpenses = this.parseDecimalInput(this.monthlyOtherExpensesDisplay()) ?? 0;
    const totalExpenses = monthlyFinancing + monthlyOtherExpenses;
    if (totalExpenses === 0) {
      return '';
    }
    return this.formatCurrency(totalExpenses);
  });

  yearlyTotalExpensesDisplay = computed(() => {
    const yearlyFinancing = this.parseDecimalInput(this.yearlyFinancingExpensesDisplay()) ?? 0;
    const yearlyOtherExpenses = this.parseDecimalInput(this.yearlyOtherExpensesDisplay()) ?? 0;
    const totalExpenses = yearlyFinancing + yearlyOtherExpenses;
    if (totalExpenses === 0) {
      return '';
    }
    return this.formatCurrency(totalExpenses);
  });

  // Scenario 1: Restschuld nach Zinsbindung
  scenario1Duration = computed(() => {
    const bindingTerm = this.parseDecimalInput(this.interestBindingTermInput()) ?? 0;
    return this.formatDecimal(bindingTerm, 1);
  });

  scenario1TotalCreditCost = computed(() => {
    const interestCost = this.parseDecimalInput(this.totalInterestDuringBindingTerm()) ?? 0;
    const amortization = this.parseDecimalInput(this.totalAmortizationOverLifetime()) ?? 0;
    return this.formatCurrency(interestCost + amortization);
  });

  scenario1TotalRunningExpenses = computed(() => {
    const yearlyOther = this.parseDecimalInput(this.yearlyOtherExpensesDisplay()) ?? 0;
    const years = this.parseDecimalInput(this.scenario1Duration()) ?? 0;
    return this.formatCurrency(yearlyOther * years);
  });

  scenario1TotalIncome = computed(() => {
    const yearlyIncome = this.parseDecimalInput(this.totalYearlyIncomeDisplay()) ?? 0;
    const years = this.parseDecimalInput(this.scenario1Duration()) ?? 0;
    return this.formatCurrency(yearlyIncome * years);
  });

  scenario1NetCashflow = computed(() => {
    const income = this.parseDecimalInput(this.scenario1TotalIncome()) ?? 0;
    const creditCost = this.parseDecimalInput(this.scenario1TotalCreditCost()) ?? 0;
    const runningExpenses = this.parseDecimalInput(this.scenario1TotalRunningExpenses()) ?? 0;
    return this.formatCurrency(income - creditCost - runningExpenses);
  });

  scenario1RemainingDebt = computed(() => {
    return this.remainingDebtAfterBindingTerm();
  });

  scenario1Equity = computed(() => {
    return this.eigenkapitalInput();
  });

  scenario1AppreciationRateInput = signal('2');
  
  scenario1PropertyValue = computed(() => {
    const purchasePrice = this.purchasePrice() ?? 0;
    const appreciationRate = this.parseDecimalInput(this.scenario1AppreciationRateInput()) ?? 0;
    const years = this.parseDecimalInput(this.scenario1Duration()) ?? 0;
    
    if (purchasePrice === 0 || years === 0) {
      return this.formatCurrency(purchasePrice);
    }
    
    // Wertsteigerung: Kaufpreis × (1 + Rate/100)^Jahre
    const futureValue = purchasePrice * Math.pow(1 + appreciationRate / 100, years);
    return this.formatCurrency(futureValue);
  });

  scenario1TotalResult = computed(() => {
    const netCashflow = this.parseDecimalInput(this.scenario1NetCashflow()) ?? 0;
    const propertyValue = this.parseDecimalInput(this.scenario1PropertyValue()) ?? 0;
    const remainingDebt = this.parseDecimalInput(this.scenario1RemainingDebt()) ?? 0;
    const eigenkapital = this.parseDecimalInput(this.eigenkapitalInput()) ?? 0;
    // Ergebnis = Netto-Cashflow + Immobilienwert - Restschuld - Eigenkapital
    return this.formatCurrency(netCashflow + propertyValue - remainingDebt - eigenkapital);
  });

  // Scenario 2: Komplett-Tilgung gleichbleibende Zinsen
  scenario2Duration = computed(() => {
    const term = this.parseDecimalInput(this.calculatedCreditTermDisplay()) ?? 0;
    return this.formatDecimal(term, 1);
  });

  scenario2TotalCreditCost = computed(() => {
    const interestCost = this.parseDecimalInput(this.totalInterestConstantRates()) ?? 0;
    const kreditbedarf = this.parseDecimalInput(this.kreditbedarfDisplay()) ?? 0;
    return this.formatCurrency(interestCost + kreditbedarf);
  });

  scenario2TotalRunningExpenses = computed(() => {
    const yearlyOther = this.parseDecimalInput(this.yearlyOtherExpensesDisplay()) ?? 0;
    const years = this.parseDecimalInput(this.scenario2Duration()) ?? 0;
    return this.formatCurrency(yearlyOther * years);
  });

  scenario2TotalIncome = computed(() => {
    const yearlyIncome = this.parseDecimalInput(this.totalYearlyIncomeDisplay()) ?? 0;
    const years = this.parseDecimalInput(this.scenario2Duration()) ?? 0;
    return this.formatCurrency(yearlyIncome * years);
  });

  scenario2NetCashflow = computed(() => {
    const income = this.parseDecimalInput(this.scenario2TotalIncome()) ?? 0;
    const creditCost = this.parseDecimalInput(this.scenario2TotalCreditCost()) ?? 0;
    const runningExpenses = this.parseDecimalInput(this.scenario2TotalRunningExpenses()) ?? 0;
    return this.formatCurrency(income - creditCost - runningExpenses);
  });

  scenario2RemainingDebt = computed(() => {
    // Szenario 2 ist vollständige Tilgung mit gleichbleibenden Zinsen - Restschuld = 0
    return this.formatCurrency(0);
  });

  scenario2Equity = computed(() => {
    return this.eigenkapitalInput();
  });

  scenario2AppreciationRateInput = signal('2');

  scenario2PropertyValue = computed(() => {
    const purchasePrice = this.purchasePrice() ?? 0;
    const appreciationRate = this.parseDecimalInput(this.scenario2AppreciationRateInput()) ?? 0;
    const years = this.parseDecimalInput(this.scenario2Duration()) ?? 0;
    
    if (purchasePrice === 0 || years === 0) {
      return this.formatCurrency(purchasePrice);
    }
    
    // Wertsteigerung: Kaufpreis × (1 + Rate/100)^Jahre
    const futureValue = purchasePrice * Math.pow(1 + appreciationRate / 100, years);
    return this.formatCurrency(futureValue);
  });

  scenario2TotalResult = computed(() => {
    const netCashflow = this.parseDecimalInput(this.scenario2NetCashflow()) ?? 0;
    const propertyValue = this.parseDecimalInput(this.scenario2PropertyValue()) ?? 0;
    const eigenkapital = this.parseDecimalInput(this.eigenkapitalInput()) ?? 0;
    // Ergebnis = Netto-Cashflow + Immobilienwert - Eigenkapital
    return this.formatCurrency(netCashflow + propertyValue - eigenkapital);
  });

  // Scenario 3: Komplett-Tilgung veränderte Zinsen
  scenario3Duration = computed(() => {
    const term = this.parseDecimalInput(this.calculatedCreditTermChangedRates()) ?? 0;
    return this.formatDecimal(term, 1);
  });

  scenario3TotalCreditCost = computed(() => {
    const interestCost = this.parseDecimalInput(this.totalInterestChangedRates()) ?? 0;
    const kreditbedarf = this.parseDecimalInput(this.kreditbedarfDisplay()) ?? 0;
    return this.formatCurrency(interestCost + kreditbedarf);
  });

  scenario3TotalRunningExpenses = computed(() => {
    const yearlyOther = this.parseDecimalInput(this.yearlyOtherExpensesDisplay()) ?? 0;
    const years = this.parseDecimalInput(this.scenario3Duration()) ?? 0;
    return this.formatCurrency(yearlyOther * years);
  });

  scenario3TotalIncome = computed(() => {
    const yearlyIncome = this.parseDecimalInput(this.totalYearlyIncomeDisplay()) ?? 0;
    const years = this.parseDecimalInput(this.scenario3Duration()) ?? 0;
    return this.formatCurrency(yearlyIncome * years);
  });

  scenario3NetCashflow = computed(() => {
    const income = this.parseDecimalInput(this.scenario3TotalIncome()) ?? 0;
    const creditCost = this.parseDecimalInput(this.scenario3TotalCreditCost()) ?? 0;
    const runningExpenses = this.parseDecimalInput(this.scenario3TotalRunningExpenses()) ?? 0;
    return this.formatCurrency(income - creditCost - runningExpenses);
  });

  scenario3RemainingDebt = computed(() => {
    // Szenario 3 ist vollständige Tilgung mit neuen Zinsen - Restschuld = 0
    return this.formatCurrency(0);
  });

  scenario3Equity = computed(() => {
    return this.eigenkapitalInput();
  });

  scenario3AppreciationRateInput = signal('2');

  scenario3PropertyValue = computed(() => {
    const purchasePrice = this.purchasePrice() ?? 0;
    const appreciationRate = this.parseDecimalInput(this.scenario3AppreciationRateInput()) ?? 0;
    const years = this.parseDecimalInput(this.scenario3Duration()) ?? 0;
    
    if (purchasePrice === 0 || years === 0) {
      return this.formatCurrency(purchasePrice);
    }
    
    // Wertsteigerung: Kaufpreis × (1 + Rate/100)^Jahre
    const futureValue = purchasePrice * Math.pow(1 + appreciationRate / 100, years);
    return this.formatCurrency(futureValue);
  });

  scenario3TotalResult = computed(() => {
    const netCashflow = this.parseDecimalInput(this.scenario3NetCashflow()) ?? 0;
    const propertyValue = this.parseDecimalInput(this.scenario3PropertyValue()) ?? 0;
    const eigenkapital = this.parseDecimalInput(this.eigenkapitalInput()) ?? 0;
    // Ergebnis = Netto-Cashflow + Immobilienwert - Eigenkapital
    return this.formatCurrency(netCashflow + propertyValue - eigenkapital);
  });

  scenariosExpanded = signal(false);
  scenarioAccordion1Expanded = signal(true);  // Variante 1 standardmäßig offen
  scenarioAccordion2Expanded = signal(false); // Variante 2
  scenarioAccordion3Expanded = signal(false); // Variante 3

  toggleScenarios() {
    this.scenariosExpanded.set(!this.scenariosExpanded());
    // When user manually interacts, mark as user-opened (blocks auto-expand)
    this.scenariosUserOpened.set(true);
  }

  toggleScenarioAccordion(variant: 1 | 2 | 3) {
    if (variant === 1) this.scenarioAccordion1Expanded.set(!this.scenarioAccordion1Expanded());
    else if (variant === 2) this.scenarioAccordion2Expanded.set(!this.scenarioAccordion2Expanded());
    else if (variant === 3) this.scenarioAccordion3Expanded.set(!this.scenarioAccordion3Expanded());
  }

  // Signals für Restschuld-Status
  isRemainingDebtNegative = computed(() => {
    const remainingDebt = this.parseDecimalInput(this.remainingDebtAfterBindingTerm()) ?? 0;
    return remainingDebt < 0;
  });

  isRemainingDebtZeroOrNegative = computed(() => {
    const remainingDebt = this.parseDecimalInput(this.remainingDebtAfterBindingTerm());
    return remainingDebt !== null && remainingDebt <= 0;
  });

  // Check if Szenario 2 exceeds 100 years (calculatedCreditTermDisplay returns empty string)
  isScenario2Over100Years = computed(() => {
    const term = this.calculatedCreditTermDisplay();
    return term === '';
  });

  // Check if Szenario 3 exceeds 100 years (calculatedCreditTermChangedRates returns empty string)
  isScenario3Over100Years = computed(() => {
    const term = this.calculatedCreditTermChangedRates();
    return term === '';
  });

  // Check if Variante 2 should be disabled
  isScenario2Disabled = computed(() => {
    const remainingDebtZeroOrNegative = this.isRemainingDebtZeroOrNegative();
    const scenario2Over100Years = this.isScenario2Over100Years();
    return remainingDebtZeroOrNegative || scenario2Over100Years;
  });

  // Check if Variante 3 should be disabled (hidden)
  isScenario3Disabled = computed(() => {
    const remainingDebtZeroOrNegative = this.isRemainingDebtZeroOrNegative();
    const scenario2Over100Years = this.isScenario2Over100Years();
    const scenario3Over100Years = this.isScenario3Over100Years();
    const changedInterestRateEmpty = !this.changedInterestRateInput()?.trim();
    return remainingDebtZeroOrNegative || scenario2Over100Years || scenario3Over100Years || changedInterestRateEmpty;
  });

  // Determine which warning message to show for scenarios
  scenariosWarningMessage = computed(() => {
    const restschuldNegative = this.isRemainingDebtZeroOrNegative();
    const scenario2Over100 = this.isScenario2Over100Years();
    const scenario3Over100 = this.isScenario3Over100Years();

    if (restschuldNegative) {
      return 'scenariosWarning';
    }

    if (scenario2Over100) {
      return 'scenariosWarning_Scenario2Over100Years';
    }

    if (scenario3Over100) {
      return 'scenariosWarning_Scenario3Over100Years';
    }

    return '';
  });

  // Determine if scenarios warning icon should be shown
  shouldShowScenariosWarningIcon = computed(() => {
    const restschuldNegative = this.isRemainingDebtZeroOrNegative();
    const scenario2Over100 = this.isScenario2Over100Years();
    const scenario3Over100 = this.isScenario3Over100Years();

    return restschuldNegative || scenario2Over100 || scenario3Over100;
  });

  scenario1NetCashflowIsPositive = computed(() => {
    return (this.parseDecimalInput(this.scenario1NetCashflow()) ?? 0) > 0;
  });

  scenario2NetCashflowIsPositive = computed(() => {
    return (this.parseDecimalInput(this.scenario2NetCashflow()) ?? 0) > 0;
  });

  scenario3NetCashflowIsPositive = computed(() => {
    return (this.parseDecimalInput(this.scenario3NetCashflow()) ?? 0) > 0;
  });

  scenario1TotalResultIsPositive = computed(() => {
    return (this.parseDecimalInput(this.scenario1TotalResult()) ?? 0) > 0;
  });

  scenario2TotalResultIsPositive = computed(() => {
    return (this.parseDecimalInput(this.scenario2TotalResult()) ?? 0) > 0;
  });

  scenario3TotalResultIsPositive = computed(() => {
    return (this.parseDecimalInput(this.scenario3TotalResult()) ?? 0) > 0;
  });

  // Computed signal to check if user is logged in (for template access)
  isLoggedIn = computed(() => this.authService.isLoggedIn());

  constructor() {
    // Effect: Listen to ToastService for Contact Form and other non-app toasts
    effect(() => {
      const serviceToast = this.toastService.toastMessage();
      if (serviceToast) {
        this.toastMessage.set(serviceToast);
        
        // Clear timeout if already running
        if (this.toastTimeoutId) {
          clearTimeout(this.toastTimeoutId);
        }
        
        // Set timeout to clear toast after 3 seconds (ToastService timeout)
        this.toastTimeoutId = window.setTimeout(() => {
          this.toastMessage.set(null);
        }, 3000);
      }
    });

    // Effect: Auto-calculate acquisition tax percentage when city changes
    effect(() => {
      const cityValue = this.city();
      const lastCityValue = this.lastCity();
      
      // Only update if city actually changed (not every render)
      if (cityValue && cityValue !== lastCityValue) {
        const taxPercentage = this.taxService.getAcquisitionTaxPercentageByCity(cityValue);
        if (taxPercentage !== null) {
          this.acquisitionTaxPercentageInput.set(this.formatDecimal(taxPercentage));
          
          // Only calculate acquisition tax in € if purchase price is set
          const purchasePrice = this.purchasePrice();
          if (purchasePrice && purchasePrice > 0) {
            const taxAmount = (purchasePrice * taxPercentage) / 100;
            this.acquisitionTaxInput.set(this.formatCurrency(taxAmount));
          }
        }
        
        // Update last city
        this.lastCity.set(cityValue);
      }
    });

    // Effect: Initialize Maklerkosten % with 3.57 ONLY ON FIRST LOAD
    effect(() => {
      const agentCostsPercentage = this.agentCostsPercentage();
      const purchasePrice = this.purchasePrice();
      
      // Only initialize if not already done AND both fields are empty
      if (!this.hasInitializedAgentCosts && (!agentCostsPercentage || agentCostsPercentage === 0)) {
        this.agentCostsPercentageInput.set(this.formatDecimal(3.57));
        
        // Calculate agentCostsInput based on purchase price if available
        if (purchasePrice && purchasePrice > 0) {
          const costs = (purchasePrice * 3.57) / 100;
          this.agentCostsInput.set(this.formatCurrency(costs));
        }
        
        this.hasInitializedAgentCosts = true;
      }
    });

    // Effect: Initialize Notarkosten % with 1.5 ONLY ON FIRST LOAD
    effect(() => {
      const notaryCostsPercentage = this.notaryCostsPercentage();
      const purchasePrice = this.purchasePrice();
      
      // Only initialize if not already done AND both fields are empty
      if (!this.hasInitializedNotaryCosts && (!notaryCostsPercentage || notaryCostsPercentage === 0)) {
        this.notaryCostsPercentageInput.set(this.formatDecimal(1.5));
        
        // Calculate notaryCostsInput based on purchase price if available
        if (purchasePrice && purchasePrice > 0) {
          const costs = (purchasePrice * 1.5) / 100;
          this.notaryCostsInput.set(this.formatCurrency(costs));
        }
        
        this.hasInitializedNotaryCosts = true;
      }
    });

    // Effect: Initialize Grundbuchamtkosten % with 0.5 ONLY ON FIRST LOAD
    effect(() => {
      const landRegistryCostsPercentage = this.landRegistryCostsPercentage();
      const purchasePrice = this.purchasePrice();
      
      // Only initialize if not already done AND both fields are empty
      if (!this.hasInitializedLandRegistryCosts && (!landRegistryCostsPercentage || landRegistryCostsPercentage === 0)) {
        this.landRegistryCostsPercentageInput.set(this.formatDecimal(0.5));
        
        // Calculate landRegistryCostsInput based on purchase price if available
        if (purchasePrice && purchasePrice > 0) {
          const costs = (purchasePrice * 0.5) / 100;
          this.landRegistryCostsInput.set(this.formatCurrency(costs));
        }
        
        this.hasInitializedLandRegistryCosts = true;
      }
    });

    // Effect: Auto-calculate property tax when property tax rate changes
    effect(() => {
      const purchasePrice = this.purchasePrice();
      const taxRate = this.propertyTaxRate();
      if (purchasePrice && purchasePrice > 0 && taxRate && taxRate > 0) {
        const tax = (purchasePrice * taxRate) / 100;
        this.propertyTaxInput.set(this.formatCurrency(tax));
      }
    });

    // Effect: Auto-calculate monthly rental price from rental price/m² and object size
    // Monatlicher Mietpreis = Objekt-Größe × Mietpreis/m²
    effect(() => {
      const rentalPrice = this.rentalPrice();
      const objectSize = this.objectSize();
      
      if (rentalPrice && rentalPrice > 0 && objectSize && objectSize > 0 && !this.monthlyRentalPriceFocused()) {
        const monthlyTotal = rentalPrice * objectSize;
        this.monthlyRentalPriceInput.set(this.formatCurrency(monthlyTotal));
      }
    });

    // Effect: Auto-calculate yearly rental price from monthly rental price
    // Jährlicher Mietpreis = Monatlicher Mietpreis × 12
    effect(() => {
      const monthly = this.monthlyRentalPrice();
      
      if (monthly && monthly > 0 && !this.yearlyRentalPriceFocused()) {
        const yearly = monthly * 12;
        this.yearlyRentalPriceInput.set(this.formatCurrency(yearly));
      }
    });

    // Effect: Auto-calculate monthly rental price from yearly rental price
    // Monatlicher Mietpreis = Jährlicher Mietpreis / 12
    effect(() => {
      const yearly = this.yearlyRentalPrice();
      
      if (yearly && yearly > 0 && !this.monthlyRentalPriceFocused()) {
        const monthly = yearly / 12;
        this.monthlyRentalPriceInput.set(this.formatCurrency(monthly));
      }
    });

    // Effect: Auto-calculate rental price/m² from monthly rental price
    // Mietpreis/m² = Monatlicher Mietpreis / Objekt-Größe
    effect(() => {
      const monthly = this.monthlyRentalPrice();
      const objectSize = this.objectSize();
      
      if (monthly && monthly > 0 && objectSize && objectSize > 0 && !this.rentalPriceFocused() && !this.monthlyRentalPriceFocused()) {
        const rentalPrice = monthly / objectSize;
        this.rentalPriceInput.set(this.formatCurrency(rentalPrice));
      }
    });

    // Effect: Auto-calculate rental price/m² from yearly rental price
    // Mietpreis/m² = (Jährlicher Mietpreis / 12) / Objekt-Größe
    effect(() => {
      const yearly = this.yearlyRentalPrice();
      const objectSize = this.objectSize();
      
      if (yearly && yearly > 0 && objectSize && objectSize > 0 && !this.rentalPriceFocused() && !this.yearlyRentalPriceFocused()) {
        const rentalPrice = (yearly / 12) / objectSize;
        this.rentalPriceInput.set(this.formatCurrency(rentalPrice));
      }
    });

    // Effect: Auto-set credit term from calculated value when dependent fields change
    effect(() => {
      const calculatedTerm = this.parseDecimalInput(this.calculatedCreditTermDisplay());
      if (calculatedTerm && calculatedTerm > 0) {
        this.creditTermInput.set(this.formatDecimal(calculatedTerm, 1));
      }
    });

    // Effect: Auto-calculate additional yearly income from additional monthly income
    // Weitere jährliche Objekt-Einnahmen = Weitere monatliche Objekt-Einnahmen × 12
    effect(() => {
      const monthly = this.additionalMonthlyIncome();
      
      if (monthly && monthly > 0 && !this.additionalYearlyIncomeFocused()) {
        const yearly = monthly * 12;
        this.additionalYearlyIncomeInput.set(this.formatCurrency(yearly));
      }
    });

    // Effect: Auto-calculate additional monthly income from additional yearly income
    // Weitere monatliche Objekt-Einnahmen = Weitere jährliche Objekt-Einnahmen / 12
    effect(() => {
      const yearly = this.additionalYearlyIncome();
      
      if (yearly && yearly > 0 && !this.additionalMonthlyIncomeFocused()) {
        const monthly = yearly / 12;
        this.additionalMonthlyIncomeInput.set(this.formatCurrency(monthly));
      }
    });

    // Effect: Detect purchase price change and recalculate € values based on %
    effect(() => {
      const currentPrice = this.purchasePrice();
      const lastPrice = this.lastPurchasePrice();
      const initialized = this.purchasePriceInitialized();
      
      // Only trigger if we've initialized AND the price actually changed
      if (initialized && currentPrice !== lastPrice) {
        // Recalculate Maklerkosten € if % is set
        const agentCostsPercentage = this.agentCostsPercentage();
        if (agentCostsPercentage && agentCostsPercentage > 0) {
          const costs = currentPrice && currentPrice > 0 ? (currentPrice * agentCostsPercentage) / 100 : 0;
          this.agentCostsInput.set(costs > 0 ? this.formatCurrency(costs) : '');
        }
        
        // Recalculate Grunderwerbsteuer € if % is set
        const acquisitionTaxPercentage = this.acquisitionTaxPercentage();
        if (acquisitionTaxPercentage && acquisitionTaxPercentage > 0) {
          const tax = currentPrice && currentPrice > 0 ? (currentPrice * acquisitionTaxPercentage) / 100 : 0;
          this.acquisitionTaxInput.set(tax > 0 ? this.formatCurrency(tax) : '');
        }
        
        // Recalculate Notarkosten € if % is set
        const notaryCostsPercentage = this.notaryCostsPercentage();
        if (notaryCostsPercentage && notaryCostsPercentage > 0) {
          const costs = currentPrice && currentPrice > 0 ? (currentPrice * notaryCostsPercentage) / 100 : 0;
          this.notaryCostsInput.set(costs > 0 ? this.formatCurrency(costs) : '');
        }
        
        // Recalculate Grundbuchamtkosten € if % is set
        const landRegistryCostsPercentage = this.landRegistryCostsPercentage();
        if (landRegistryCostsPercentage && landRegistryCostsPercentage > 0) {
          const costs = currentPrice && currentPrice > 0 ? (currentPrice * landRegistryCostsPercentage) / 100 : 0;
          this.landRegistryCostsInput.set(costs > 0 ? this.formatCurrency(costs) : '');
        }
      }
      
      // Update last price and mark as initialized
      this.lastPurchasePrice.set(currentPrice ?? 0);
      if (!initialized) {
        this.purchasePriceInitialized.set(true);
      }
    });

    // Effect: Auto-expand Kaufkosten when ALL address fields are filled AND user hasn't manually opened it
    effect(() => {
      const city = this.city();
      const address = this.address();
      const postalCode = this.postalCode();
      const userOpened = this.purchaseCostsUserOpened();
      
      // Auto-expand only if: ALL address fields filled AND user hasn't manually opened this segment
      if (city && address && postalCode && !userOpened) {
        this.purchaseCostsExpanded.set(true);
        // Mark as auto-opened (set to true to block further auto-expand)
        this.purchaseCostsUserOpened.set(true);
      }
    });

    // Effect: Auto-expand Objekt-Einkommen when ALL purchase costs fields are filled AND user hasn't manually opened it
    effect(() => {
      const purchasePrice = this.purchasePrice();
      const userOpened = this.objectIncomeUserOpened();
      
      // Auto-expand only if: purchase price filled AND user hasn't manually opened this segment
      if (purchasePrice && purchasePrice > 0 && !userOpened) {
        this.objectIncomeExpanded.set(true);
        // Mark as auto-opened
        this.objectIncomeUserOpened.set(true);
      }
    });

    // Effect: Auto-expand Finanzierung when ALL object income fields are filled AND user hasn't manually opened it
    effect(() => {
      const monthlyRentalPrice = this.monthlyRentalPrice();
      const userOpened = this.financingUserOpened();
      
      // Auto-expand only if: monthly rental price filled AND user hasn't manually opened this segment
      if (monthlyRentalPrice && monthlyRentalPrice > 0 && !userOpened) {
        this.financingExpanded.set(true);
        // Mark as auto-opened
        this.financingUserOpened.set(true);
      }
    });

    // Effect: Auto-expand Laufende Ausgaben when ALL financing fields are filled AND user hasn't manually opened it
    effect(() => {
      const interestRate = this.interestRate();
      const userOpened = this.runningExpensesUserOpened();
      
      // Auto-expand only if: interest rate filled AND user hasn't manually opened this segment
      if (interestRate && interestRate > 0 && !userOpened) {
        this.runningExpensesExpanded.set(true);
        // Mark as auto-opened
        this.runningExpensesUserOpened.set(true);
      }
    });

    // Effect: Auto-expand Amortisation when ALL running expenses fields are filled AND user hasn't manually opened it
    effect(() => {
      const adminCosts = this.adminCosts();
      const userOpened = this.amortisationUserOpened();
      
      // Auto-expand only if: admin costs filled AND user hasn't manually opened this segment
      if (adminCosts && adminCosts > 0 && !userOpened) {
        this.amortisationExpanded.set(true);
        // Mark as auto-opened
        this.amortisationUserOpened.set(true);
      }
    });

    // Effect: Auto-expand Szenarien-Vergleich when Amortisation is expanded AND user hasn't manually opened it
    effect(() => {
      const amortisationExpanded = this.amortisationExpanded();
      const userOpened = this.scenariosUserOpened();
      
      // Auto-expand only if: Amortisation is expanded AND user hasn't manually opened this segment
      if (amortisationExpanded && !userOpened) {
        this.scenariosExpanded.set(true);
        // Mark as auto-opened
        this.scenariosUserOpened.set(true);
      }
    });

    // Effect: Synchronize help section heights with form section heights
    effect(() => {
      // Access all expansion states to trigger re-calculation when they change
      this.addressDataExpanded();
      this.purchaseCostsExpanded();
      this.objectIncomeExpanded();
      this.financingExpanded();
      this.runningExpensesExpanded();
      this.amortisationExpanded();

      // Only run in browser environment
      if (typeof document === 'undefined') {
        return;
      }

      // Use setTimeout with longer delay to wait for DOM to fully update
      setTimeout(() => {
        // Title/Placeholder (form 0 -> help placeholder 0)
        const titleForm = document.querySelectorAll('.form-column > .calculator-container')[0];
        const titlePlaceholder = document.querySelectorAll('.help-sidebar > div')[0]; // Direct child div (placeholder)
        
        if (titleForm && titlePlaceholder) {
          const formHeight = titleForm.getBoundingClientRect().height;
          console.log('Title Form Height:', formHeight);
          (titlePlaceholder as HTMLElement).style.setProperty('min-height', formHeight + 'px', 'important');
        }

        // Adressdaten (form 1 -> help-section 0 which is actually the first .help-section)
        const adressdatenForm = document.querySelectorAll('.form-column > .calculator-container')[1];
        const adressdatenHelp = document.querySelectorAll('.help-sidebar .help-section')[0];
        
        if (adressdatenForm && adressdatenHelp) {
          const formHeight = adressdatenForm.getBoundingClientRect().height;
          console.log('Adressdaten Form Height:', formHeight);
          (adressdatenHelp as HTMLElement).style.setProperty('min-height', formHeight + 'px', 'important');
        }

        // Kaufkosten (form 2 -> help-section 1)
        const kaufkostenForm = document.querySelectorAll('.form-column > .calculator-container')[2];
        const kaufkostenHelp = document.querySelectorAll('.help-sidebar .help-section')[1];
        
        if (kaufkostenForm && kaufkostenHelp) {
          const formHeight = kaufkostenForm.getBoundingClientRect().height;
          console.log('Kaufkosten Form Height:', formHeight);
          (kaufkostenHelp as HTMLElement).style.setProperty('min-height', formHeight + 'px', 'important');
        }

        // Objekt-Einnahmen (form 3 -> help-section 2)
        const objektForm = document.querySelectorAll('.form-column > .calculator-container')[3];
        const objektHelp = document.querySelectorAll('.help-sidebar .help-section')[2];
        
        if (objektForm && objektHelp) {
          const formHeight = objektForm.getBoundingClientRect().height;
          console.log('Objekt-Einnahmen Form Height:', formHeight);
          (objektHelp as HTMLElement).style.setProperty('min-height', formHeight + 'px', 'important');
        }

        // Finanzierung (form 4 -> help-section 3)
        const finForm = document.querySelectorAll('.form-column > .calculator-container')[4];
        const finHelp = document.querySelectorAll('.help-sidebar .help-section')[3];
        
        if (finForm && finHelp) {
          const formHeight = finForm.getBoundingClientRect().height;
          console.log('Finanzierung Form Height:', formHeight);
          (finHelp as HTMLElement).style.setProperty('min-height', formHeight + 'px', 'important');
        }

        // Laufende Ausgaben (form 5 -> help-section 4)
        // Height controlled by inline styles in HTML template only
        // No Effect override needed

        // Amortisation (form 6 -> help-section 5)
        // Only set expanded height when section is actually open
        if (this.amortisationExpanded()) {
          const amortForm = document.querySelectorAll('.form-column > .calculator-container')[6];
          const amortHelp = document.querySelectorAll('.help-sidebar .help-section')[5];
          
          if (amortForm && amortHelp) {
            // Measure the actual full height needed for all content
            const scrollHeight = (amortForm as HTMLElement).scrollHeight;
            console.log('Amortisation scrollHeight (expanded):', scrollHeight);
            // Set both form and help to the full scroll height
            (amortForm as HTMLElement).style.setProperty('min-height', scrollHeight + 'px', 'important');
            (amortHelp as HTMLElement).style.setProperty('min-height', scrollHeight + 'px', 'important');
          }
        } else {
          // When collapsed, reset to standard size
          const amortForm = document.querySelectorAll('.form-column > .calculator-container')[6];
          const amortHelp = document.querySelectorAll('.help-sidebar .help-section')[5];
          
          if (amortForm && amortHelp) {
            (amortForm as HTMLElement).style.removeProperty('min-height');
            (amortForm as HTMLElement).style.removeProperty('max-height');
            (amortHelp as HTMLElement).style.removeProperty('min-height');
            (amortHelp as HTMLElement).style.removeProperty('max-height');
          }
        }
      }, 200); // 200ms delay für vollständigen DOM-Update
    });

    // Effect: Auto-load latest calculation after login
    effect(() => {
      const isLoggedIn = this.authService.isLoggedIn();
      
      if (isLoggedIn) {
        // Load latest calculation asynchronously
        this.calculationStorageService.getLatestCalculation().subscribe({
          next: (calculation) => {
            if (calculation) {
              this.loadCalculationData(calculation);
            }
          },
          error: (error) => {
            console.error('Failed to auto-load latest calculation:', error);
          }
        });
      }
    });

    // Effect: Clear changed interest rate input when remaining debt becomes <= 0
    effect(() => {
      if (this.isRemainingDebtZeroOrNegative()) {
        this.changedInterestRateInput.set('');
      }
    });
  }

  // Helper: Smart Scoring für Relevanz-Berechnung
  private calculateRelevance(text: string, query: string): number {
    const lowerText = text.toLowerCase().trim();
    const lowerQuery = query.toLowerCase().trim();
    
    // Exakter Match
    if (lowerText === lowerQuery) return 1000;
    
    // Beginnt mit Query (perfekt!)
    if (lowerText.startsWith(lowerQuery)) return 100;
    
    // Query ist Beginn eines Wortes (z.B. "New York" bei Query "new")
    const words = lowerText.split(/[\s\-_/]/);
    if (words.some(w => w.startsWith(lowerQuery))) return 80;
    
    // Query liegt irgendwo im Text
    if (lowerText.includes(lowerQuery)) return 40;
    
    // Keine Ähnlichkeit
    return 0;
  }

  // Country methods
  updateCountry(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.country.set(input.value);
    this.showCountrySuggestions.set(true);

    if (this.countryDebounceTimer) {
      clearTimeout(this.countryDebounceTimer);
    }

    if (input.value.length >= 3) {
      this.isLoadingCountries.set(true);
      this.countryDebounceTimer = setTimeout(() => {
        this.searchCountries(input.value);
      }, 300);
    } else {
      this.countrySuggestions.set([]);
      this.isLoadingCountries.set(false);
      // Kaskadierendes Löschen: Wenn Land gelöscht wird, auch Stadt, Straße und PLZ löschen
      this.city.set('');
      this.address.set('');
      this.postalCode.set('');
      this.citySuggestions.set([]);
      this.addressSuggestions.set([]);
    }
  }

  private searchCountries(query: string): void {
    console.log('Searching countries:', query);
    
    // Get suggestions from CountrySearchService (searches internal database)
    const countryNames = this.countrySearchService.searchCountries(query);
    
    // Convert country names to NominatimResult format for template compatibility
    const suggestions: NominatimResult[] = countryNames.map(name => ({
      display_name: name,
      address: { country: name }
    }));
    
    this.countrySuggestions.set(suggestions);
    this.isLoadingCountries.set(false);
  }

  selectCountry(suggestion: NominatimResult): void {
    this.country.set(suggestion.address?.country || suggestion.display_name);
    this.countrySuggestions.set([]);
    this.showCountrySuggestions.set(false);
    this.city.set('');
    this.address.set('');
  }

  hideCountrySuggestions(): void {
    setTimeout(() => {
      this.showCountrySuggestions.set(false);
    }, 200);
  }

  // City methods
  updateCity(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.city.set(input.value);
    this.showCitySuggestions.set(true);

    if (this.cityDebounceTimer) {
      clearTimeout(this.cityDebounceTimer);
    }

    if (input.value.length >= 3 && this.country()) {
      this.isLoadingCities.set(true);
      this.cityDebounceTimer = setTimeout(() => {
        this.searchCities(input.value);
      }, 300);
    } else {
      this.citySuggestions.set([]);
      this.isLoadingCities.set(false);
      // Kaskadierendes Löschen: Wenn Stadt gelöscht wird, auch Straße und PLZ löschen
      this.address.set('');
      this.postalCode.set('');
      this.addressSuggestions.set([]);
    }
  }

  private searchCities(query: string): void {
    // Nominatim Parameter optimiert: limit=50 für bessere Treffer bei kurzen Queries (z.B. "ber" → Berlin)
    const countryQuery = this.country() ? `, ${this.country()}` : '';
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query + countryQuery)}&format=json&addressdetails=1&limit=50&featuretype=city,town,village`;
    
    console.log('Searching cities with URL:', url);
    
    fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'User-Agent': 'REACT-Calculator-App'
      }
    })
      .then(response => {
        console.log('Response status:', response.status);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then((results: NominatimResult[]) => {
        console.log('City results:', results);
        
        // Smart Scoring: Bewerte und sortiere nach Relevanz
        const scoredResults = results
          .map(result => ({
            ...result,
            relevanceScore: this.calculateRelevance(
              result.address?.city || result.address?.town || result.address?.village || result.display_name,
              query
            )
          }))
          .sort((a, b) => b.relevanceScore - a.relevanceScore)
          .filter(r => r.relevanceScore > 0);
        
        // Deduplication: Nach Name (da Städte nicht doppelt mit PLZ vorkommen)
        const seenNames = new Set<string>();
        const dedupedResults = scoredResults
          .filter(result => {
            const name = result.address?.city || result.address?.town || result.address?.village || result.display_name;
            if (seenNames.has(name)) return false;
            seenNames.add(name);
            return true;
          })
          .slice(0, 5);
        
        // Entferne relevanceScore aus Endergebnis
        this.citySuggestions.set(dedupedResults.map(({ relevanceScore, ...rest }) => rest));
        this.isLoadingCities.set(false);
      })
      .catch((error) => {
        console.error('City search error:', error);
        this.citySuggestions.set([]);
        this.isLoadingCities.set(false);
      });
  }

  selectCity(suggestion: NominatimResult): void {
    this.city.set(
      suggestion.address?.city ||
      suggestion.address?.town ||
      suggestion.address?.village ||
      suggestion.display_name
    );
    this.postalCode.set(suggestion.address?.postcode || '');
    this.citySuggestions.set([]);
    this.showCitySuggestions.set(false);
    this.address.set('');
  }

  hideCitySuggestions(): void {
    setTimeout(() => {
      this.showCitySuggestions.set(false);
    }, 200);
  }

  // Address methods
  updateAddress(event: Event): void {
    const input = event.target as HTMLInputElement;
    const newValue = input.value;
    this.address.set(newValue);
    this.showAddressSuggestions.set(true);

    if (this.addressDebounceTimer) {
      clearTimeout(this.addressDebounceTimer);
    }

    if (newValue.length >= 3 && this.city()) {
      this.isLoadingAddresses.set(true);
      this.addressDebounceTimer = setTimeout(() => {
        this.searchAddresses(newValue);
      }, 300);
    } else {
      this.addressSuggestions.set([]);
      this.isLoadingAddresses.set(false);
      // Kaskadierendes Löschen: Wenn Straße gelöscht wird, auch PLZ löschen
      this.postalCode.set('');
    }
  }

  private searchAddresses(query: string): void {
    // Nominatim Parameter optimiert: limit=50 für bessere Treffer bei kurzen Queries (z.B. "son" → Sonnenallee)
    const cityQuery = this.city() ? `, ${this.city()}` : '';
    const countryQuery = this.country() ? `, ${this.country()}` : '';
    const url = `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query + cityQuery + countryQuery)}&format=json&addressdetails=1&limit=50&featuretype=way`;
    
    console.log('Searching addresses with URL:', url);
    
    fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'User-Agent': 'REACT-Calculator-App'
      }
    })
      .then(response => {
        console.log('Address response status:', response.status);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then((results: NominatimResult[]) => {
        console.log('Address results:', results);
        
        // Smart Scoring: Bewerte und sortiere nach Relevanz
        const scoredResults = results
          .filter(result => result.address?.road || result.address?.house_number)
          .map(result => ({
            ...result,
            relevanceScore: this.calculateRelevance(
              result.address?.road || result.display_name,
              query
            )
          }))
          .sort((a, b) => b.relevanceScore - a.relevanceScore)
          .filter(r => r.relevanceScore > 0);
        
        // Deduplication: Nach Name + Postleitzahl (behält unterschiedliche PLZ!)
        const seenKeys = new Set<string>();
        const dedupedResults = scoredResults
          .filter(result => {
            // Key = Straße + PLZ (damit z.B. "Hauptstraße Ber" und "Hauptstraße Hamburg" beide angezeigt werden)
            const key = `${result.address?.road || result.display_name}|${result.address?.postcode || ''}`;
            if (seenKeys.has(key)) return false;
            seenKeys.add(key);
            return true;
          })
          .slice(0, 5);
        
        // Entferne relevanceScore aus Endergebnis
        this.addressSuggestions.set(dedupedResults.map(({ relevanceScore, ...rest }) => rest));
        this.isLoadingAddresses.set(false);
      })
      .catch((error) => {
        console.error('Address search error:', error);
        this.addressSuggestions.set([]);
        this.isLoadingAddresses.set(false);
      });
  }

  selectAddress(suggestion: NominatimResult): void {
    const addressParts = [
      suggestion.address?.road || '',
      suggestion.address?.house_number || ''
    ].filter(part => part.length > 0);
    
    const addressValue = addressParts.join(' ').trim() || suggestion.display_name.split(',')[0];
    
    this.address.set(addressValue);
    this.postalCode.set(suggestion.address?.postcode || '');
    this.addressSuggestions.set([]);
    this.showAddressSuggestions.set(false);
  }

  hideAddressSuggestions(): void {
    setTimeout(() => {
      this.showAddressSuggestions.set(false);
    }, 200);
  }

  // Purchase price methods
  updatePurchasePrice(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.purchasePriceInput.set(input.value);
  }

  // Acquisition tax methods
  updateAcquisitionTax(event: Event): void {
    if (this.isUpdatingAcquisitionTax) return;
    
    const input = event.target as HTMLInputElement;
    const value = input.value;
    
    this.acquisitionTaxInput.set(value);
    
    const parsedValue = this.parseDecimalInput(value) ?? 0;
    // If the value is 0 or empty, reset the percentage to 0,00
    if (parsedValue === 0 || value.trim() === '') {
      this.isUpdatingAcquisitionTax = true;
      this.acquisitionTaxPercentageInput.set('0,00');
      this.isUpdatingAcquisitionTax = false;
    } else {
      // Otherwise, calculate the percentage
      const purchasePrice = this.purchasePrice();
      if (purchasePrice && purchasePrice > 0) {
        const percentage = (parsedValue / purchasePrice) * 100;
        this.isUpdatingAcquisitionTax = true;
        this.acquisitionTaxPercentageInput.set(this.formatDecimal(percentage));
        this.isUpdatingAcquisitionTax = false;
      }
    }
  }


  updateAcquisitionTaxPercentage(event: Event): void {
    if (this.isUpdatingAcquisitionTax) return;
    
    const input = event.target as HTMLInputElement;
    const value = input.value;
    
    this.acquisitionTaxPercentageInput.set(value);
    
    const parsedValue = this.parseDecimalInput(value) ?? 0;
    // If the value is 0 or empty, reset the € to 0,00
    if (parsedValue === 0 || value.trim() === '') {
      this.isUpdatingAcquisitionTax = true;
      this.acquisitionTaxInput.set('0,00');
      this.isUpdatingAcquisitionTax = false;
      // Clear DOM field
      const euroField = document.getElementById('acquisitionTax') as HTMLInputElement;
      if (euroField) euroField.value = '';
    } else {
      // Otherwise, calculate the € amount
      const purchasePrice = this.purchasePrice();
      if (purchasePrice && purchasePrice > 0) {
        const taxAmount = (purchasePrice * parsedValue) / 100;
        this.isUpdatingAcquisitionTax = true;
        this.acquisitionTaxInput.set(this.formatCurrency(taxAmount));
        this.isUpdatingAcquisitionTax = false;
      }
    }
  }

  // Notary costs methods
  updateNotaryCosts(event: Event): void {
    if (this.isUpdatingNotaryCosts) return;
    
    const input = event.target as HTMLInputElement;
    const value = input.value;
    
    this.notaryCostsInput.set(value);
    
    const parsedValue = this.parseDecimalInput(value) ?? 0;
    // If the value is 0 or empty, reset the percentage to 0,00
    if (parsedValue === 0 || value.trim() === '') {
      this.isUpdatingNotaryCosts = true;
      this.notaryCostsPercentageInput.set('0,00');
      this.isUpdatingNotaryCosts = false;
    } else {
      // Otherwise, calculate the percentage
      const purchasePrice = this.purchasePrice();
      if (purchasePrice && purchasePrice > 0) {
        const percentage = (parsedValue / purchasePrice) * 100;
        this.isUpdatingNotaryCosts = true;
        this.notaryCostsPercentageInput.set(this.formatDecimal(percentage));
        this.isUpdatingNotaryCosts = false;
      }
    }
  }


  updateNotaryCostsPercentage(event: Event): void {
    if (this.isUpdatingNotaryCosts) return;
    
    const input = event.target as HTMLInputElement;
    const value = input.value;
    
    this.notaryCostsPercentageInput.set(value);
    
    const parsedValue = this.parseDecimalInput(value) ?? 0;
    // If the value is 0 or empty, reset the € to 0,00
    if (parsedValue === 0 || value.trim() === '') {
      this.isUpdatingNotaryCosts = true;
      this.notaryCostsInput.set('0,00');
      this.isUpdatingNotaryCosts = false;
      // Clear DOM field
      const euroField = document.getElementById('notaryCosts') as HTMLInputElement;
      if (euroField) euroField.value = '';
    } else {
      // Otherwise, calculate the € amount
      const purchasePrice = this.purchasePrice();
      if (purchasePrice && purchasePrice > 0) {
        const costAmount = (purchasePrice * parsedValue) / 100;
        this.isUpdatingNotaryCosts = true;
        this.notaryCostsInput.set(this.formatCurrency(costAmount));
        this.isUpdatingNotaryCosts = false;
      }
    }
  }

  // Land registry costs methods
  updateLandRegistryCosts(event: Event): void {
    if (this.isUpdatingLandRegistryCosts) return;
    
    const input = event.target as HTMLInputElement;
    const value = input.value;
    
    this.landRegistryCostsInput.set(value);
    
    const parsedValue = this.parseDecimalInput(value) ?? 0;
    // If the value is 0 or empty, reset the percentage to 0,00
    if (parsedValue === 0 || value.trim() === '') {
      this.isUpdatingLandRegistryCosts = true;
      this.landRegistryCostsPercentageInput.set('0,00');
      this.isUpdatingLandRegistryCosts = false;
    } else {
      // Otherwise, calculate the percentage
      const purchasePrice = this.purchasePrice();
      if (purchasePrice && purchasePrice > 0) {
        const percentage = (parsedValue / purchasePrice) * 100;
        this.isUpdatingLandRegistryCosts = true;
        this.landRegistryCostsPercentageInput.set(this.formatDecimal(percentage));
        this.isUpdatingLandRegistryCosts = false;
      }
    }
  }


  updateLandRegistryCostsPercentage(event: Event): void {
    if (this.isUpdatingLandRegistryCosts) return;
    
    const input = event.target as HTMLInputElement;
    const value = input.value;
    
    this.landRegistryCostsPercentageInput.set(value);
    
    const parsedValue = this.parseDecimalInput(value) ?? 0;
    // If the value is 0 or empty, reset the € to 0,00
    if (parsedValue === 0 || value.trim() === '') {
      this.isUpdatingLandRegistryCosts = true;
      this.landRegistryCostsInput.set('0,00');
      this.isUpdatingLandRegistryCosts = false;
      // Clear DOM field
      const euroField = document.getElementById('landRegistryCosts') as HTMLInputElement;
      if (euroField) euroField.value = '';
    } else {
      // Otherwise, calculate the € amount
      const purchasePrice = this.purchasePrice();
      if (purchasePrice && purchasePrice > 0) {
        const costAmount = (purchasePrice * parsedValue) / 100;
        this.isUpdatingLandRegistryCosts = true;
        this.landRegistryCostsInput.set(this.formatCurrency(costAmount));
        this.isUpdatingLandRegistryCosts = false;
      }
    }
  }

  // Agent costs methods
  updateAgentCosts(event: Event): void {
    if (this.isUpdatingAgentCosts) return;
    
    const input = event.target as HTMLInputElement;
    const value = input.value;
    
    this.agentCostsInput.set(value);
    
    const parsedValue = this.parseDecimalInput(value) ?? 0;
    // If the value is 0 or empty, reset the percentage to 0,00
    if (parsedValue === 0 || value.trim() === '') {
      this.isUpdatingAgentCosts = true;
      this.agentCostsPercentageInput.set('0,00');
      this.isUpdatingAgentCosts = false;
    } else {
      // Otherwise, calculate the percentage
      const purchasePrice = this.purchasePrice();
      if (purchasePrice && purchasePrice > 0) {
        const percentage = (parsedValue / purchasePrice) * 100;
        this.isUpdatingAgentCosts = true;
        this.agentCostsPercentageInput.set(this.formatDecimal(percentage));
        this.isUpdatingAgentCosts = false;
      }
    }
  }


  updateAgentCostsPercentage(event: Event): void {
    if (this.isUpdatingAgentCosts) return;
    
    const input = event.target as HTMLInputElement;
    const value = input.value;
    
    this.agentCostsPercentageInput.set(value);
    
    const parsedValue = this.parseDecimalInput(value) ?? 0;
    // If the value is 0 or empty, reset the € to 0,00
    if (parsedValue === 0 || value.trim() === '') {
      this.isUpdatingAgentCosts = true;
      this.agentCostsInput.set('0,00');
      this.isUpdatingAgentCosts = false;
      // Clear DOM field
      const euroField = document.getElementById('agentCosts') as HTMLInputElement;
      if (euroField) euroField.value = '';
    } else {
      // Otherwise, calculate the € amount
      const purchasePrice = this.purchasePrice();
      if (purchasePrice && purchasePrice > 0) {
        const costAmount = (purchasePrice * parsedValue) / 100;
        this.isUpdatingAgentCosts = true;
        this.agentCostsInput.set(this.formatCurrency(costAmount));
        this.isUpdatingAgentCosts = false;
      }
    }
  }

  // Other costs methods
  updateOtherCosts(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.otherCostsInput.set(input.value);
  }

  updateEigenkapital(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.eigenkapitalInput.set(input.value);
  }

  // Property tax methods
  updatePropertyTaxRate(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.propertyTaxRateInput.set(input.value);
  }

  updatePropertyTax(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.propertyTaxInput.set(input.value);
  }

  // Object size methods
  updateObjectSize(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.objectSizeInput.set(input.value);
  }

  /**
   * Baujahr-Eingabe. Akzeptiert nur Ziffern; leer = unbekannt.
   * Range-Validierung (1800..aktuelles Jahr) erfolgt in constructionYearNumber().
   */
  updateConstructionYear(event: Event): void {
    const input = event.target as HTMLInputElement;
    // Nur Ziffern zulassen (max 4)
    const cleaned = input.value.replace(/\D/g, '').slice(0, 4);
    if (cleaned !== input.value) {
      input.value = cleaned;
    }
    this.constructionYearInput.set(cleaned);
  }

  /**
   * Blur-Validierung Baujahr: Ung\u00fcltige Werte (<1800 oder > aktuelles Jahr)
   * werden geleert und der User bekommt einen Hinweis. Leerer Wert bleibt erlaubt (=unbekannt).
   */
  onConstructionYearBlur(event: Event): void {
    const input = event.target as HTMLInputElement;
    const raw = input.value.trim();
    if (!raw) return; // leer = unbekannt, erlaubt
    const n = parseInt(raw, 10);
    const currentYear = new Date().getFullYear();
    if (isNaN(n) || n < 1800 || n > currentYear) {
      this.constructionYearInput.set('');
      input.value = '';
      this.showToast(`Baujahr muss zwischen 1800 und ${currentYear} liegen.`, 'warning');
    }
  }

  /** Max erlaubter Baujahr-Wert (aktuelles Jahr). Für HTML [max]-Binding. */
  currentYear = new Date().getFullYear();

  // Postal code methods
  updatePostalCode(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.postalCode.set(input.value);
  }

  // Rental price methods
  updateRentalPrice(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.rentalPriceInput.set(input.value);
    
    const parsedValue = this.parseDecimalInput(input.value) ?? 0;
    
    console.log('DEBUG-updateRentalPrice:', {
      inputValue: input.value,
      parsedValue: parsedValue,
      step: 'after parseDecimalInput'
    });

    // If the value is 0 or empty, reset both monthly and yearly rental prices to 0,00
    if (parsedValue === 0 || input.value.trim() === '') {
      this.monthlyRentalPriceInput.set('0,00');
      this.yearlyRentalPriceInput.set('0,00');
      const monthlyField = document.getElementById('monthlyRentalPrice') as HTMLInputElement;
      const yearlyField = document.getElementById('yearlyRentalPrice') as HTMLInputElement;
      if (monthlyField) monthlyField.value = '';
      if (yearlyField) yearlyField.value = '';
    } else {
      // Otherwise, calculate monthly (same as rental price) and yearly (rental price * 12)
      const formatted = this.formatCurrency(parsedValue);
      console.log('DEBUG-updateRentalPrice-formatted:', { parsedValue, formatted });
      console.log('DEBUG-BEFORE-SET:', {
        rentalPriceInput: this.rentalPriceInput(),
        monthlyRentalPriceInput: this.monthlyRentalPriceInput(),
        yearlyRentalPriceInput: this.yearlyRentalPriceInput()
      });
      this.monthlyRentalPriceInput.set(formatted);
      const yearlyValue = parsedValue * 12;
      this.yearlyRentalPriceInput.set(this.formatCurrency(yearlyValue));
      console.log('DEBUG-AFTER-SET:', {
        rentalPriceInput: this.rentalPriceInput(),
        monthlyRentalPriceInput: this.monthlyRentalPriceInput(),
        yearlyRentalPriceInput: this.yearlyRentalPriceInput()
      });
    }
  }

  updateMonthlyRentalPrice(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.monthlyRentalPriceInput.set(input.value);
    
    const parsedValue = this.parseDecimalInput(input.value) ?? 0;
    // If the value is 0 or empty, reset both rental price and yearly rental price to 0,00
    if (parsedValue === 0 || input.value.trim() === '') {
      this.rentalPriceInput.set('0,00');
      this.yearlyRentalPriceInput.set('0,00');
      const priceField = document.getElementById('rentalPrice') as HTMLInputElement;
      const yearlyField = document.getElementById('yearlyRentalPrice') as HTMLInputElement;
      if (priceField) priceField.value = '';
      if (yearlyField) yearlyField.value = '';
    } else {
      // Otherwise, calculate rental price (same as monthly) and yearly (monthly * 12)
      this.rentalPriceInput.set(this.formatCurrency(parsedValue));
      const yearlyValue = parsedValue * 12;
      this.yearlyRentalPriceInput.set(this.formatCurrency(yearlyValue));
    }
  }

  updateYearlyRentalPrice(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.yearlyRentalPriceInput.set(input.value);
    
    const parsedValue = this.parseDecimalInput(input.value) ?? 0;
    // If the value is 0 or empty, reset both rental price and monthly rental price to 0,00
    if (parsedValue === 0 || input.value.trim() === '') {
      this.rentalPriceInput.set('0,00');
      this.monthlyRentalPriceInput.set('0,00');
      const priceField = document.getElementById('rentalPrice') as HTMLInputElement;
      const monthlyField = document.getElementById('monthlyRentalPrice') as HTMLInputElement;
      if (priceField) priceField.value = '';
      if (monthlyField) monthlyField.value = '';
    } else {
      // Otherwise, calculate monthly (yearly / 12) and rental price (same as monthly)
      const monthlyValue = parsedValue / 12;
      this.rentalPriceInput.set(this.formatCurrency(monthlyValue));
      this.monthlyRentalPriceInput.set(this.formatCurrency(monthlyValue));
    }
  }

  // Additional income methods
  updateAdditionalMonthlyIncome(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.additionalMonthlyIncomeInput.set(input.value);
    
    const parsedValue = this.parseDecimalInput(input.value) ?? 0;
    // If the value is 0 or empty, reset the yearly income to 0,00
    if (parsedValue === 0 || input.value.trim() === '') {
      this.additionalYearlyIncomeInput.set('0,00');
      const yearlyField = document.getElementById('additionalYearlyIncome') as HTMLInputElement;
      if (yearlyField) yearlyField.value = '';
    } else {
      // Otherwise, calculate the yearly value (monthly * 12)
      const yearlyValue = parsedValue * 12;
      this.additionalYearlyIncomeInput.set(this.formatCurrency(yearlyValue));
    }
  }

  updateAdditionalYearlyIncome(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.additionalYearlyIncomeInput.set(input.value);
    
    const parsedValue = this.parseDecimalInput(input.value) ?? 0;
    // If the value is 0 or empty, reset the monthly income to 0,00
    if (parsedValue === 0 || input.value.trim() === '') {
      this.additionalMonthlyIncomeInput.set('0,00');
      const monthlyField = document.getElementById('additionalMonthlyIncome') as HTMLInputElement;
      if (monthlyField) monthlyField.value = '';
    } else {
      // Otherwise, calculate the monthly value (yearly / 12)
      const monthlyValue = parsedValue / 12;
      this.additionalMonthlyIncomeInput.set(this.formatCurrency(monthlyValue));
    }
  }

  // Financing input methods
  updateCreditTerm(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.creditTermInput.set(input.value);
  }

  updateAmortizationRate(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.amortizationRateInput.set(input.value);
    this.lastFinancingFieldChanged.set('amortizationRate');
    // Clearwise monthlyRateInput wenn user Anfängliche Tilgung ändert
    this.monthlyRateInput.set('');
  }

  updateMonthlyRate(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.monthlyRateInput.set(input.value);
    this.lastFinancingFieldChanged.set('monthlyRate');
    
    // Clear amortizationRateInput wenn user Kreditrate ändert
    this.amortizationRateInput.set('');
  }

  updateInterestRate(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.interestRateInput.set(input.value);
  }

  updateChangedInterestRate(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.changedInterestRateInput.set(input.value);
  }

  updateInterestBindingTerm(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.interestBindingTermInput.set(input.value);
  }

  updateAnnualExtraRepayment(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.annualExtraRepaymentInput.set(input.value);
  }

  // Running expenses methods
  updateAdminCosts(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.adminCostsInput.set(input.value);
  }

  updateTaxCosts(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.taxCostsInput.set(input.value);
  }

  updateHouseMoney(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.houseMoneyInput.set(input.value);
  }

  updateInsurance(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.insuranceInput.set(input.value);
  }

  updateRepairCosts(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.repairCostsInput.set(input.value);
  }

  updateOtherExpenses(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.otherExpensesInput.set(input.value);
  }

  // Section toggle methods
  toggleAddressData(): void {
    console.log('toggleAddressData called, current value:', this.addressDataExpanded());
    this.addressDataExpanded.update(v => !v);
    console.log('new value:', this.addressDataExpanded());
  }

  togglePurchaseCosts(): void {
    const newValue = !this.purchaseCostsExpanded();
    this.purchaseCostsExpanded.set(newValue);
    // When user manually interacts, mark as user-opened (blocks auto-expand)
    this.purchaseCostsUserOpened.set(true);
  }

  toggleObjectIncome(): void {
    const newValue = !this.objectIncomeExpanded();
    this.objectIncomeExpanded.set(newValue);
    // When user manually interacts, mark as user-opened (blocks auto-expand)
    this.objectIncomeUserOpened.set(true);
  }

  toggleFinancing(): void {
    const newValue = !this.financingExpanded();
    this.financingExpanded.set(newValue);
    // When user manually interacts, mark as user-opened (blocks auto-expand)
    this.financingUserOpened.set(true);
  }

  toggleRunningExpenses(): void {
    const newValue = !this.runningExpensesExpanded();
    this.runningExpensesExpanded.set(newValue);
    // When user manually interacts, mark as user-opened (blocks auto-expand)
    this.runningExpensesUserOpened.set(true);
  }

  toggleAmortisation(): void {
    const newValue = !this.amortisationExpanded();
    this.amortisationExpanded.set(newValue);
    // When user manually interacts, mark as user-opened (blocks auto-expand)
    this.amortisationUserOpened.set(true);
  }

  updateScenario1AppreciationRate(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    if (value !== null) {
      if (value < -100) {
        input.value = '-100,00';
        this.scenario1AppreciationRateInput.set('-100,00');
        return;
      }
      if (value > 999.99) {
        input.value = '999,99';
        this.scenario1AppreciationRateInput.set('999,99');
        return;
      }
    }
    this.scenario1AppreciationRateInput.set(input.value);
  }

  updateScenario2AppreciationRate(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    if (value !== null) {
      if (value < -100) {
        input.value = '-100,00';
        this.scenario2AppreciationRateInput.set('-100,00');
        return;
      }
      if (value > 999.99) {
        input.value = '999,99';
        this.scenario2AppreciationRateInput.set('999,99');
        return;
      }
    }
    this.scenario2AppreciationRateInput.set(input.value);
  }

  updateScenario3AppreciationRate(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    if (value !== null) {
      if (value < -100) {
        input.value = '-100,00';
        this.scenario3AppreciationRateInput.set('-100,00');
        return;
      }
      if (value > 999.99) {
        input.value = '999,99';
        this.scenario3AppreciationRateInput.set('999,99');
        return;
      }
    }
    this.scenario3AppreciationRateInput.set(input.value);
  }

  // Number input methods for calculator
  updateNumber1(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.number1Display.set(input.value);
  }

  updateNumber2(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.number2Display.set(input.value);
  }

  // Calculate method
  calculate(): void {
    if (this.number1() === null || this.number2() === null) {
      this.error.set('Bitte geben Sie zwei Zahlen ein.');
      return;
    }
    this.isLoading.set(true);
    this.error.set('');

    setTimeout(() => {
      const result = (this.number1() ?? 0) + (this.number2() ?? 0);
      this.resultDisplay.set(result.toString());
      this.isLoading.set(false);
    }, 500);
  }

  // Keyboard event handlers
  onRentalPriceKeyPress(event: KeyboardEvent): void {
    const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', ',', 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
    if (!allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  onDecimalKeyPress(event: KeyboardEvent): void {
    const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', ',', 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'];
    if (!allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  // Utility methods
  private getCurrentCalculationData(): CalculationData {
    return {
      purchasePrice: this.purchasePriceInput(),
      agentCosts: this.agentCostsInput(),
      agentCostsPercentage: this.agentCostsPercentageInput(),
      acquisitionTax: this.acquisitionTaxInput(),
      acquisitionTaxPercentage: this.acquisitionTaxPercentageInput(),
      notaryCosts: this.notaryCostsInput(),
      notaryCostsPercentage: this.notaryCostsPercentageInput(),
      landRegistryCosts: this.landRegistryCostsInput(),
      landRegistryCostsPercentage: this.landRegistryCostsPercentageInput(),
      otherCosts: this.otherCostsInput(),
      propertyTaxRate: this.propertyTaxRateInput(),
      propertyTax: this.propertyTaxInput(),
      eigenkapital: this.eigenkapitalInput(),
      creditTerm: this.creditTermInput(),
      amortizationRate: this.amortizationRateInput(),
      interestRate: this.interestRateInput(),
      interestBindingTerm: this.interestBindingTermInput(),
      annualExtraRepayment: this.annualExtraRepaymentInput(),
      monthlyRate: this.monthlyRateInput(),
      changedInterestRate: this.changedInterestRateInput(),
      adminCosts: this.adminCostsInput(),
      taxCosts: this.taxCostsInput(),
      houseMoney: this.houseMoneyInput(),
      insurance: this.insuranceInput(),
      repairCosts: this.repairCostsInput(),
      otherExpenses: this.otherExpensesInput(),
      objectSize: this.objectSizeInput(),
      constructionYear: this.constructionYearNumber(),
      rentalPrice: this.rentalPriceInput(),
      monthlyRentalPrice: this.monthlyRentalPriceInput(),
      yearlyRentalPrice: this.yearlyRentalPriceInput(),
      additionalMonthlyIncome: this.additionalMonthlyIncomeInput(),
      additionalYearlyIncome: this.additionalYearlyIncomeInput(),
      scenario1AppreciationRate: this.scenario1AppreciationRateInput(),
      scenario2AppreciationRate: this.scenario2AppreciationRateInput(),
      scenario3AppreciationRate: this.scenario3AppreciationRateInput(),
    };
  }

  private hasAnyInput(data: CalculationData): boolean {
    // Prüfe ob irgend ein Feld nicht leer ist
    const values = Object.values(data);
    return values.some(val => val && val !== '0' && val !== '0,00' && val !== '');
  }

  private parseDecimalInput(value: string): number | null {
    if (!value) return null;
    // Step 1: Remove all dots (thousand separators): "123.456,77" → "123456,77"
    const withoutThousands = value.replace(/\./g, '');
    // Step 2: Replace decimal comma with point: "123456,77" → "123456.77"
    const normalized = withoutThousands.replace(',', '.');
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? null : parsed;
  }

  private formatDecimal(value: number, decimalPlaces: number = 2): string {
    if (value === 0) return '';
    return value.toLocaleString('de-DE', {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
      useGrouping: false,
    });
  }

  // Format currency values with thousands separator (German style: 1.234.567,89)
  // Private version for internal use
  private formatCurrency(value: number, decimalPlaces: number = 2): string {
    if (value === 0) return '';
    return value.toLocaleString('de-DE', {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
      useGrouping: true,
    });
  }

  // Public method for template use
  formatNumberWithThousands(value: number | string): string {
    const numValue = typeof value === 'string' ? (this.parseDecimalInput(value) ?? 0) : value;
    if (numValue === 0) return '0,00';
    return numValue.toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true,
    });
  }

  // Currency input formatting handlers
  // On blur: Add thousand separators for better readability
  onCurrencyBlur(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    
    if (value !== null && value !== 0) {
      // Format with thousand separators
      input.value = this.formatCurrency(value);
    } else if (value === 0 || !input.value.trim()) {
      // Clear empty/zero values
      input.value = '';
    }
  }

  // On focus: Remove thousand separators for easier editing
  onCurrencyFocus(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    
    if (value !== null && value !== 0) {
      // Format without thousand separators
      input.value = this.formatDecimal(value);
    }
  }

  // Individual blur handlers for [value] bound fields to update signals
  onAgentCostsBlur(event: Event): void {
    this.onCurrencyBlur(event);
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    this.agentCostsInput.set(value !== null ? this.formatCurrency(value) : '');
  }

  onAcquisitionTaxBlur(event: Event): void {
    this.onCurrencyBlur(event);
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    this.acquisitionTaxInput.set(value !== null ? this.formatCurrency(value) : '');
  }

  onNotaryCostsBlur(event: Event): void {
    this.onCurrencyBlur(event);
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    this.notaryCostsInput.set(value !== null ? this.formatCurrency(value) : '');
  }

  onLandRegistryCostsBlur(event: Event): void {
    this.onCurrencyBlur(event);
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    this.landRegistryCostsInput.set(value !== null ? this.formatCurrency(value) : '');
  }

  onOtherCostsBlur(event: Event): void {
    this.onCurrencyBlur(event);
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    this.otherCostsInput.set(value !== null ? this.formatCurrency(value) : '');
  }

  onEigenkapitalBlur(event: Event): void {
    this.onCurrencyBlur(event);
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    this.eigenkapitalInput.set(value !== null ? this.formatCurrency(value) : '');
  }

  onRentalPriceBlur(event: Event): void {
    this.rentalPriceFocused.set(false);
    this.onCurrencyBlur(event);
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    this.rentalPriceInput.set(value !== null ? this.formatCurrency(value) : '');
  }

  onRentalPriceFocus(event: Event): void {
    this.rentalPriceFocused.set(true);
    this.onCurrencyFocus(event);
  }

  onMonthlyRentalPriceBlur(event: Event): void {
    this.monthlyRentalPriceFocused.set(false);
    this.onCurrencyBlur(event);
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    this.monthlyRentalPriceInput.set(value !== null ? this.formatCurrency(value) : '');
  }

  onMonthlyRentalPriceFocus(event: Event): void {
    this.monthlyRentalPriceFocused.set(true);
    this.onCurrencyFocus(event);
  }

  onYearlyRentalPriceBlur(event: Event): void {
    this.yearlyRentalPriceFocused.set(false);
    this.onCurrencyBlur(event);
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    this.yearlyRentalPriceInput.set(value !== null ? this.formatCurrency(value) : '');
  }

  onYearlyRentalPriceFocus(event: Event): void {
    this.yearlyRentalPriceFocused.set(true);
    this.onCurrencyFocus(event);
  }

  onAdditionalMonthlyIncomeBlur(event: Event): void {
    this.additionalMonthlyIncomeFocused.set(false);
    this.onCurrencyBlur(event);
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    this.additionalMonthlyIncomeInput.set(value !== null ? this.formatCurrency(value) : '');
  }

  onAdditionalMonthlyIncomeFocus(event: Event): void {
    this.additionalMonthlyIncomeFocused.set(true);
    this.onCurrencyFocus(event);
  }

  onAdditionalYearlyIncomeBlur(event: Event): void {
    this.additionalYearlyIncomeFocused.set(false);
    this.onCurrencyBlur(event);
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    this.additionalYearlyIncomeInput.set(value !== null ? this.formatCurrency(value) : '');
  }

  onAdditionalYearlyIncomeFocus(event: Event): void {
    this.additionalYearlyIncomeFocused.set(true);
    this.onCurrencyFocus(event);
  }

  onAnnualExtraRepaymentBlur(event: Event): void {
    this.onCurrencyBlur(event);
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    this.annualExtraRepaymentInput.set(value !== null ? this.formatCurrency(value) : '');
  }

  onAdminCostsBlur(event: Event): void {
    this.onCurrencyBlur(event);
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    this.adminCostsInput.set(value !== null ? this.formatCurrency(value) : '');
  }

  onTaxCostsBlur(event: Event): void {
    this.onCurrencyBlur(event);
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    this.taxCostsInput.set(value !== null ? this.formatCurrency(value) : '');
  }

  onHouseMoneyBlur(event: Event): void {
    this.onCurrencyBlur(event);
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    this.houseMoneyInput.set(value !== null ? this.formatCurrency(value) : '');
  }

  onInsuranceBlur(event: Event): void {
    this.onCurrencyBlur(event);
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    this.insuranceInput.set(value !== null ? this.formatCurrency(value) : '');
  }

  onRepairCostsBlur(event: Event): void {
    this.onCurrencyBlur(event);
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    this.repairCostsInput.set(value !== null ? this.formatCurrency(value) : '');
  }

  onOtherExpensesBlur(event: Event): void {
    this.onCurrencyBlur(event);
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    this.otherExpensesInput.set(value !== null ? this.formatCurrency(value) : '');
  }

  onMonthlyRateBlur(event: Event): void {
    this.onCurrencyBlur(event);
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    this.monthlyRateInput.set(value !== null ? this.formatCurrency(value) : '');
  }

  onObjectSizeBlur(event: Event): void {
    this.onCurrencyBlur(event);
    const input = event.target as HTMLInputElement;
    const value = this.parseDecimalInput(input.value);
    this.objectSizeInput.set(value !== null ? this.formatCurrency(value) : '');
  }

  // Calculation Storage Methods
  openSaveDialog(): void {
    const currentId = this.currentCalculationId();
    
    // If existing calculation, save directly without dialog
    if (currentId) {
      this.saveCalculationDirectly();
      return;
    }
    
    // Otherwise, show dialog for new calculation
    const dialog = this.saveDialog();
    if (dialog) {
      const data = this.getCalculationData();
      const addressData = {
        country: this.country(),
        city: this.city(),
        address: this.address(),
        postalCode: this.postalCode()
      };
      
      dialog.openForNew(data, addressData);
      
      dialog.onSaved.set((id: number, name: string) => {
        // Update current calculation ID and name after save
        this.currentCalculationId.set(id);
        this.currentCalculationName.set(name);
        this.showToast('Berechnung gespeichert', 'success');
        console.log('Berechnung gespeichert mit ID:', id, 'Name:', name);
      });
      
      // Toast-Callback für Validierungsfehler setzen
      dialog.onToast.set((message: string, type: 'success' | 'error') => {
        this.showToast(message, type);
      });
    }
  }

  private async saveCalculationDirectly(): Promise<void> {
    try {
      const data = this.getCalculationData();
      const addressData = {
        country: this.country(),
        city: this.city(),
        address: this.address(),
        postalCode: this.postalCode()
      };
      
      const request = {
        name: this.currentCalculationName(),
        country: addressData.country,
        city: addressData.city,
        address: addressData.address,
        postalCode: addressData.postalCode,
        calculationData: data
      };
      
      const currentId = this.currentCalculationId();
      if (currentId) {
        await this.calculationStorageService.updateCalculation(currentId, request).toPromise();
        this.showToast('Berechnung gespeichert', 'success');
        console.log('Berechnung aktualisiert mit ID:', currentId);
        // Mark as saved (dirty check reset)
        this.lastSavedCalculationData.set(this.getCurrentCalculationData());
      }
    } catch (error: any) {
      console.error('Fehler beim Speichern:', error);
      
      // Prüfe ob es ein Validierungsfehler ist
      if (error?.error?.ValidationError) {
        const message = error.error.Message || 'Die Berechnung konnte nicht gespeichert werden. Einige Werte sind zu extrem.';
        this.showToast(message, 'error');
        return;
      }
      
      this.showToast('Fehler beim Speichern: ' + (error?.error?.message || error?.message || 'Unbekannter Fehler'), 'error');
    }
  }

  private showToast(message: string, type: 'success' | 'error' | 'ai' | 'warning'): void {
    this.toastMessage.set({ text: message, type });
    
    // Clearen alten Timeout wenn vorhanden
    if (this.toastTimeoutId) {
      clearTimeout(this.toastTimeoutId);
    }
    
    // Neuen Timeout mit ID speichern
    this.toastTimeoutId = window.setTimeout(() => {
      this.toastMessage.set(null);
    }, 7000); // Toast verschwindet nach 7 Sekunden
  }

  openLoadDialog(): void {
    const dialog = this.loadDialog();
    if (dialog) {
      dialog.open();
      dialog.onSelected.set(async (id: number) => {
        try {
          const calculation = await this.calculationStorageService.getCalculation(id).toPromise();
          if (calculation) {
            this.loadCalculationData(calculation);
            this.showToast('Berechnung geladen', 'success');
            console.log('Berechnung erfolgreich geladen');
          }
        } catch (error) {
          console.error('Fehler beim Laden:', error);
          this.showToast('Fehler beim Laden der Berechnung', 'error');
        }
      });
      
      // Toast-Callback für Lösch-Benachrichtigungen setzen
      dialog.onToast.set((message: string) => {
        this.showToast(message, 'success');
      });
      
      // onDeleted-Callback setzen - leere Berechnung anzeigen wenn aktuelle gelöscht wurde
      dialog.onDeleted.set((deletedId: number) => {
        if (deletedId === this.currentCalculationId()) {
          this.newCalculation();
        }
      });
    }
  }

  openExportDialog(): void {
    // Check if PDF export feature is available
    if (!this.subscriptionService.hasFeature('PDF_EXPORT')) {
      this.showToast('Diese Funktion ist in deinem aktuellen Abonnement nicht verfügbar. Aktualisiere dein Abonnement, um diese Funktion freizuschalten.', 'warning');
      return;
    }

    // Check if calculation needs saving first
    if (this.isCalculationModified()) {
      this.showToast('Die Berechnung wurde geändert. Bitte speichern Sie zuerst!', 'warning');
      return;
    }

    // Check if calculation is loaded
    const currentId = this.currentCalculationId();
    if (!currentId) {
      this.showToast('⚠️ Bitte speichern Sie die Berechnung zuerst!', 'error');
      return;
    }

    const dialog = this.exportCalculationDialog();
    if (dialog) {
      dialog.open(currentId);
    }
  }

  openComparisonExportDialog(): void {
    // Check if PDF export feature is available
    if (!this.subscriptionService.hasFeature('PDF_EXPORT')) {
      this.showToast('Diese Funktion ist in deinem aktuellen Abonnement nicht verfügbar. Aktualisiere dein Abonnement, um diese Funktion freizuschalten.', 'warning');
      return;
    }

    // Check if current calculation needs saving first
    if (this.isCalculationModified()) {
      this.showToast('Die Berechnung wurde geändert. Bitte speichern Sie zuerst!', 'warning');
      return;
    }

    const dialog = this.exportComparisonDialog();
    if (dialog) {
      dialog.open();
    }
  }

  toggleActionMenu(): void {
    this.actionMenuOpen.update(open => !open);
  }

  closeActionMenu(): void {
    this.actionMenuOpen.set(false);
  }

  toggleNameDropdown(): void {
    this.nameDropdownOpen.update(open => !open);
  }

  closeNameDropdown(): void {
    this.nameDropdownOpen.set(false);
  }

  openRenameDialog(): void {
    this.closeNameDropdown();
    const dialog = this.renameDialog();
    if (dialog && this.currentCalculationId() && this.currentCalculationName()) {
      dialog.open(this.currentCalculationId()!, this.currentCalculationName());
      dialog.onRenamed.set((newName: string) => {
        this.currentCalculationName.set(newName);
        this.showToast('Berechnung erfolgreich umbenannt', 'success');
      });
    }
  }

  openDeleteDialog(): void {
    this.closeNameDropdown();
    const dialog = this.deleteDialog();
    if (dialog && this.currentCalculationName()) {
      dialog.open(this.currentCalculationName());
      dialog.onConfirmed.set(async () => {
        try {
          const id = this.currentCalculationId();
          if (id) {
            await this.calculationStorageService.deleteCalculation(id).toPromise();
            this.showToast('Berechnung erfolgreich gelöscht', 'success');
            // Leere Berechnung anzeigen
            this.newCalculation();
            dialog.close();
          }
        } catch (error: any) {
          console.error('Fehler beim Löschen:', error);
          this.showToast('Fehler beim Löschen: ' + (error?.error?.message || 'Unbekannter Fehler'), 'error');
          dialog.close();
        }
      });
    }
  }

  openDuplicateDialog(): void {
    this.closeNameDropdown();
    const dialog = this.duplicateDialog();
    if (dialog && this.currentCalculationId() && this.currentCalculationName()) {
      // Hole alle Berechnungen für Namensvalidierung
      this.calculationStorageService.getMyCalculations().subscribe({
        next: (calculations) => {
          const allNames = calculations.map(c => c.name);
          dialog.open(this.currentCalculationId()!, this.currentCalculationName(), allNames);
          dialog.onDuplicated.set(async (newId: number) => {
            try {
              const calculation = await this.calculationStorageService.getCalculation(newId).toPromise();
              if (calculation) {
                this.loadCalculationData(calculation);
                this.showToast('Berechnung erfolgreich dupliziert', 'success');
              }
            } catch (error) {
              console.error('Fehler beim Laden der duplizierten Berechnung:', error);
              this.showToast('Duplikat wurde erstellt, konnte aber nicht geladen werden', 'error');
            }
          });
        },
        error: (error) => {
          console.error('Fehler beim Abrufen der Berechnungen:', error);
          this.showToast('Fehler beim Duplizieren', 'error');
        }
      });
    }
  }

  newCalculation(): void {
    // Reset all form fields
    this.country.set('Deutschland');
    this.city.set('');
    this.address.set('');
    this.postalCode.set('');
    
    // Clear all input fields
    this.purchasePriceInput.set('');
    this.agentCostsInput.set('');
    this.agentCostsPercentageInput.set('');
    this.acquisitionTaxInput.set('');
    this.acquisitionTaxPercentageInput.set('');
    this.notaryCostsInput.set('');
    this.notaryCostsPercentageInput.set('');
    this.landRegistryCostsInput.set('');
    this.landRegistryCostsPercentageInput.set('');
    this.otherCostsInput.set('');
    this.propertyTaxRateInput.set('');
    this.propertyTaxInput.set('');
    this.eigenkapitalInput.set('');
    this.creditTermInput.set('');
    this.amortizationRateInput.set('');
    this.interestRateInput.set('');
    this.interestBindingTermInput.set('');
    this.annualExtraRepaymentInput.set('');
    this.monthlyRateInput.set('');
    this.changedInterestRateInput.set('');
    this.adminCostsInput.set('');
    this.taxCostsInput.set('');
    this.houseMoneyInput.set('');
    this.insuranceInput.set('');
    this.repairCostsInput.set('');
    this.otherExpensesInput.set('');
    this.objectSizeInput.set('');
    this.rentalPriceInput.set('');
    this.monthlyRentalPriceInput.set('');
    this.yearlyRentalPriceInput.set('');
    this.additionalMonthlyIncomeInput.set('');
    this.additionalYearlyIncomeInput.set('');
    
    // Clear scenario appreciation rates - set default value 2
    this.scenario1AppreciationRateInput.set('2');
    this.scenario2AppreciationRateInput.set('2');
    this.scenario3AppreciationRateInput.set('2');
    
    // Reset current calculation state
    this.currentCalculationId.set(null);
    this.currentCalculationName.set('');
    
    // Reset initialization flags to allow default values to be set again
    this.hasInitializedAgentCosts = false;
    this.hasInitializedNotaryCosts = false;
    this.hasInitializedLandRegistryCosts = false;
    
    // Reset user-opened flags to allow auto-expansion again
    this.purchaseCostsUserOpened.set(false);
    this.objectIncomeUserOpened.set(false);
    this.financingUserOpened.set(false);
    this.runningExpensesUserOpened.set(false);
    this.amortisationUserOpened.set(false);
    this.scenariosUserOpened.set(false);
    
    // Reset section expansion states
    this.addressDataExpanded.set(true);
    this.purchaseCostsExpanded.set(false);
    this.objectIncomeExpanded.set(false);
    this.financingExpanded.set(false);
    this.runningExpensesExpanded.set(false);
    this.amortisationExpanded.set(false);
    this.scenariosExpanded.set(false);
  }

  getCalculationData(): CalculationData {
    // Prüfe ob Restschuld <= 0 (dann sollten Scenario 2 & 3 nicht gespeichert werden)
    const isRestschuldZeroOrNegative = this.isRemainingDebtZeroOrNegative();
    // Prüfe ob "Veränderter Jahreszins" Feld gefüllt ist (notwendig für gültige Scenario 3)
    const hasValidChangedInterestRate = !!this.changedInterestRateInput()?.trim();

    return {
      // Input-Felder
      purchasePrice: this.purchasePriceInput(),
      agentCosts: this.agentCostsInput(),
      agentCostsPercentage: this.agentCostsPercentageInput(),
      acquisitionTax: this.acquisitionTaxInput(),
      acquisitionTaxPercentage: this.acquisitionTaxPercentageInput(),
      notaryCosts: this.notaryCostsInput(),
      notaryCostsPercentage: this.notaryCostsPercentageInput(),
      landRegistryCosts: this.landRegistryCostsInput(),
      landRegistryCostsPercentage: this.landRegistryCostsPercentageInput(),
      otherCosts: this.otherCostsInput(),
      eigenkapital: this.eigenkapitalInput(),
      creditTerm: this.creditTermInput(),
      amortizationRate: this.amortizationRateInput(),
      interestRate: this.interestRateInput(),
      interestBindingTerm: this.interestBindingTermInput(),
      annualExtraRepayment: this.annualExtraRepaymentInput(),
      monthlyRate: this.monthlyRateDisplay(),
      changedInterestRate: this.changedInterestRateInput(),
      adminCosts: this.adminCostsInput(),
      taxCosts: this.taxCostsInput(),
      houseMoney: this.houseMoneyInput(),
      insurance: this.insuranceInput(),
      repairCosts: this.repairCostsInput(),
      otherExpenses: this.otherExpensesInput(),
      objectSize: this.objectSizeInput(),
      constructionYear: this.constructionYearNumber(),
      rentalPrice: this.rentalPriceInput(),
      monthlyRentalPrice: this.monthlyRentalPriceInput(),
      yearlyRentalPrice: this.yearlyRentalPriceInput(),
      additionalMonthlyIncome: this.additionalMonthlyIncomeInput(),
      additionalYearlyIncome: this.additionalYearlyIncomeInput(),
      
      // Berechnete Gesamtwerte
      totalPurchaseCosts: this.totalBuyingCostsDisplay(),
      kreditbedarf: this.kreditbedarfDisplay(),
      totalYearlyIncome: this.totalYearlyIncomeDisplay(),
      totalMonthlyExpenses: this.monthlyTotalExpensesDisplay(),
      totalYearlyExpenses: this.yearlyTotalExpensesDisplay(),
      
      // Erweiterte Berechnungen
      kaufpreisProQuadratmeter: this.kaufpreisProQuadratmeterDisplay(),
      xfachVonKaufpreis: this.xfachVonKaufpreisDisplay(),
      xfachVonGesamtKaufkosten: this.xfachVonGesamtKaufkostenDisplay(),
      amortizationAmount: this.amortizationAmountDisplay(),
      interestAmount: this.interestAmountDisplay(),
      monthlyAmortization: this.monthlyAmortizationDisplay(),
      monthlyInterest: this.monthlyInterestDisplay(),
      totalAmortizationOverLifetime: this.totalAmortizationOverLifetime(),
      totalInterestOverLifetime: this.totalInterestOverLifetime(),
      totalPaymentOverLifetime: this.totalPaymentOverLifetime(),
      totalCreditCostIncludingRemainingDebt: this.totalCreditCostIncludingRemainingDebt(),
      totalInterestConstantRates: this.totalInterestConstantRates(),
      totalPaymentOverLifetimeConstantRates: this.totalPaymentOverLifetimeConstantRates(),
      totalInterestChangedRates: (isRestschuldZeroOrNegative || !hasValidChangedInterestRate) ? '' : this.totalInterestChangedRates(),
      totalPaymentOverLifetimeChangedRates: (isRestschuldZeroOrNegative || !hasValidChangedInterestRate) ? '' : this.totalPaymentOverLifetimeChangedRates(),
      totalInterestDuringBindingTerm: this.totalInterestDuringBindingTerm(),
      remainingDebtAfterBindingTerm: this.remainingDebtAfterBindingTerm(),
      monthlyIncome: this.monthlyIncomeDisplay(),
      monthlyFinancingExpenses: this.monthlyFinancingExpensesDisplay(),
      monthlyOtherExpenses: this.monthlyOtherExpensesDisplay(),
      monthlyIncomeExcess: this.monthlyIncomeExcessDisplay(),
      yearlyIncomeExcess: this.yearlyIncomeExcessDisplay(),
      yearlyFinancingExpenses: this.yearlyFinancingExpensesDisplay(),
      yearlyOtherExpenses: this.yearlyOtherExpensesDisplay(),
      calculatedCreditTerm: this.calculatedCreditTermDisplay(),
      calculatedCreditTermChangedRates: (isRestschuldZeroOrNegative || !hasValidChangedInterestRate) ? '' : this.calculatedCreditTermChangedRates(),
      
      // Szenarien Appreciation Rates (genau das speichern, was im Feld steht)
      scenario1AppreciationRate: this.scenario1AppreciationRateInput(),
      scenario2AppreciationRate: isRestschuldZeroOrNegative ? '' : this.scenario2AppreciationRateInput(),
      scenario3AppreciationRate: (isRestschuldZeroOrNegative || !hasValidChangedInterestRate) ? '' : this.scenario3AppreciationRateInput(),
      
      // Szenario 1 berechnete Werte
      scenario1Duration: this.scenario1Duration(),
      scenario1TotalCreditCost: this.scenario1TotalCreditCost(),
      scenario1TotalRunningExpenses: this.scenario1TotalRunningExpenses(),
      scenario1TotalIncome: this.scenario1TotalIncome(),
      scenario1NetCashflow: this.scenario1NetCashflow(),
      scenario1RemainingDebt: this.scenario1RemainingDebt() || '0',
      scenario1PropertyValue: this.scenario1PropertyValue(),
      scenario1TotalResult: this.scenario1TotalResult(),
      
      // Szenario 2 berechnete Werte (leeren wenn Restschuld <= 0)
      scenario2Duration: isRestschuldZeroOrNegative ? '' : this.scenario2Duration(),
      scenario2TotalCreditCost: isRestschuldZeroOrNegative ? '' : this.scenario2TotalCreditCost(),
      scenario2TotalRunningExpenses: isRestschuldZeroOrNegative ? '' : this.scenario2TotalRunningExpenses(),
      scenario2TotalIncome: isRestschuldZeroOrNegative ? '' : this.scenario2TotalIncome(),
      scenario2NetCashflow: isRestschuldZeroOrNegative ? '' : this.scenario2NetCashflow(),
      scenario2RemainingDebt: isRestschuldZeroOrNegative ? '' : (this.scenario2RemainingDebt() || '0'),
      scenario2PropertyValue: isRestschuldZeroOrNegative ? '' : this.scenario2PropertyValue(),
      scenario2TotalResult: isRestschuldZeroOrNegative ? '' : this.scenario2TotalResult(),
      
      // Szenario 3 berechnete Werte (leeren wenn Restschuld <= 0 ODER kein Zinssatz eingegeben)
      scenario3Duration: (isRestschuldZeroOrNegative || !hasValidChangedInterestRate) ? '' : this.scenario3Duration(),
      scenario3TotalCreditCost: (isRestschuldZeroOrNegative || !hasValidChangedInterestRate) ? '' : this.scenario3TotalCreditCost(),
      scenario3TotalRunningExpenses: (isRestschuldZeroOrNegative || !hasValidChangedInterestRate) ? '' : this.scenario3TotalRunningExpenses(),
      scenario3TotalIncome: (isRestschuldZeroOrNegative || !hasValidChangedInterestRate) ? '' : this.scenario3TotalIncome(),
      scenario3NetCashflow: (isRestschuldZeroOrNegative || !hasValidChangedInterestRate) ? '' : this.scenario3NetCashflow(),
      scenario3RemainingDebt: (isRestschuldZeroOrNegative || !hasValidChangedInterestRate) ? '' : (this.scenario3RemainingDebt() || '0'),
      scenario3PropertyValue: (isRestschuldZeroOrNegative || !hasValidChangedInterestRate) ? '' : this.scenario3PropertyValue(),
      scenario3TotalResult: (isRestschuldZeroOrNegative || !hasValidChangedInterestRate) ? '' : this.scenario3TotalResult(),
    };
  }

  loadCalculationData(calculation: any): void {
    // Schritt 1: Alle Input-Felder zuerst leeren (verhindert alte Werte)
    this.purchasePriceInput.set('');
    this.agentCostsInput.set('');
    this.agentCostsPercentageInput.set('');
    this.acquisitionTaxInput.set('');
    this.acquisitionTaxPercentageInput.set('');
    this.notaryCostsInput.set('');
    this.notaryCostsPercentageInput.set('');
    this.landRegistryCostsInput.set('');
    this.landRegistryCostsPercentageInput.set('');
    this.otherCostsInput.set('');
    this.eigenkapitalInput.set('');
    this.creditTermInput.set('');
    this.amortizationRateInput.set('');
    this.interestRateInput.set('');
    this.interestBindingTermInput.set('');
    this.annualExtraRepaymentInput.set('');
    this.monthlyRateInput.set('');
    this.changedInterestRateInput.set('');
    this.adminCostsInput.set('');
    this.taxCostsInput.set('');
    this.houseMoneyInput.set('');
    this.insuranceInput.set('');
    this.repairCostsInput.set('');
    this.otherExpensesInput.set('');
    this.objectSizeInput.set('');
    this.constructionYearInput.set('');
    this.rentalPriceInput.set('');
    this.monthlyRentalPriceInput.set('');
    this.yearlyRentalPriceInput.set('');
    this.additionalMonthlyIncomeInput.set('');
    this.additionalYearlyIncomeInput.set('');
    this.scenario1AppreciationRateInput.set('');
    this.scenario2AppreciationRateInput.set('');
    this.scenario3AppreciationRateInput.set('');
    
    // Schritt 2: Neue Daten laden
    const data = calculation.calculationData;
    
    // Set current calculation ID and name
    this.currentCalculationId.set(calculation.calculationId);
    this.currentCalculationName.set(calculation.name);
    
    // Adressdaten
    this.country.set(calculation.country || 'Deutschland');
    this.city.set(calculation.city || '');
    this.address.set(calculation.address || '');
    this.postalCode.set(calculation.postalCode || '');
    
    // Input-Felder
    if (data.purchasePrice) this.purchasePriceInput.set(data.purchasePrice);
    if (data.agentCosts) this.agentCostsInput.set(data.agentCosts);
    if (data.agentCostsPercentage) this.agentCostsPercentageInput.set(data.agentCostsPercentage);
    if (data.acquisitionTax) this.acquisitionTaxInput.set(data.acquisitionTax);
    if (data.acquisitionTaxPercentage) this.acquisitionTaxPercentageInput.set(data.acquisitionTaxPercentage);
    if (data.notaryCosts) this.notaryCostsInput.set(data.notaryCosts);
    if (data.notaryCostsPercentage) this.notaryCostsPercentageInput.set(data.notaryCostsPercentage);
    if (data.landRegistryCosts) this.landRegistryCostsInput.set(data.landRegistryCosts);
    if (data.landRegistryCostsPercentage) this.landRegistryCostsPercentageInput.set(data.landRegistryCostsPercentage);
    if (data.otherCosts) this.otherCostsInput.set(data.otherCosts);
    if (data.eigenkapital) this.eigenkapitalInput.set(data.eigenkapital);
    if (data.creditTerm) this.creditTermInput.set(data.creditTerm);
    if (data.amortizationRate) this.amortizationRateInput.set(data.amortizationRate);
    if (data.interestRate) this.interestRateInput.set(data.interestRate);
    if (data.interestBindingTerm) this.interestBindingTermInput.set(data.interestBindingTerm);
    if (data.annualExtraRepayment) this.annualExtraRepaymentInput.set(data.annualExtraRepayment);
    if (data.monthlyRate) this.monthlyRateInput.set(data.monthlyRate);
    if (data.changedInterestRate) this.changedInterestRateInput.set(data.changedInterestRate);
    if (data.adminCosts) this.adminCostsInput.set(data.adminCosts);
    if (data.taxCosts) this.taxCostsInput.set(data.taxCosts);
    if (data.houseMoney) this.houseMoneyInput.set(data.houseMoney);
    if (data.insurance) this.insuranceInput.set(data.insurance);
    if (data.repairCosts) this.repairCostsInput.set(data.repairCosts);
    if (data.otherExpenses) this.otherExpensesInput.set(data.otherExpenses);
    if (data.objectSize) this.objectSizeInput.set(data.objectSize);
    if (data.constructionYear != null) this.constructionYearInput.set(String(data.constructionYear));
    if (data.rentalPrice) this.rentalPriceInput.set(data.rentalPrice);
    if (data.monthlyRentalPrice) this.monthlyRentalPriceInput.set(data.monthlyRentalPrice);
    if (data.yearlyRentalPrice) this.yearlyRentalPriceInput.set(data.yearlyRentalPrice);
    if (data.additionalMonthlyIncome) this.additionalMonthlyIncomeInput.set(data.additionalMonthlyIncome);
    if (data.additionalYearlyIncome) this.additionalYearlyIncomeInput.set(data.additionalYearlyIncome);
    
    // Szenarien Appreciation Rates (nur laden, was da ist, kein Fallback)
    if (data.scenario1AppreciationRate) this.scenario1AppreciationRateInput.set(data.scenario1AppreciationRate);
    if (data.scenario2AppreciationRate) this.scenario2AppreciationRateInput.set(data.scenario2AppreciationRate);
    if (data.scenario3AppreciationRate) this.scenario3AppreciationRateInput.set(data.scenario3AppreciationRate);
    
    // Format Euro fields with thousand separators after DOM update
    setTimeout(() => {
      this.formatLoadedCurrencyFields();
      // Mark as saved (dirty check reset)
      this.lastSavedCalculationData.set(this.getCurrentCalculationData());
    }, 0);
  }
  
  // Format all currency input fields with thousand separators after loading
  private formatLoadedCurrencyFields(): void {
    const currencyFields = [
      'purchasePrice', 'agentCosts', 'acquisitionTax', 'notaryCosts', 
      'landRegistryCosts', 'otherCosts', 'eigenkapital', 'rentalPrice',
      'monthlyRentalPrice', 'yearlyRentalPrice', 'additionalMonthlyIncome',
      'additionalYearlyIncome', 'annualExtraRepayment', 'adminCosts',
      'taxCosts', 'houseMoney', 'insurance', 'repairCosts', 'otherExpenses',
      'monthlyRate', 'objectSize'
    ];
    
    currencyFields.forEach(fieldId => {
      const input = document.getElementById(fieldId) as HTMLInputElement;
      if (input && input.value) {
        const value = this.parseDecimalInput(input.value);
        if (value !== null && value !== 0) {
          input.value = this.formatCurrency(value);
        }
      }
    });
  }
  
  // Tooltip methods for mobile help
  showTooltip(fieldId: string, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.activeTooltip.set(fieldId);
  }
  
  hideTooltip() {
    this.activeTooltip.set(null);
  }
  
  isTooltipActive(fieldId: string): boolean {
    return this.activeTooltip() === fieldId;
  }

  // Warning methods for field-specific alerts
  activeWarning = signal<string | null>(null);

  showFieldWarning(fieldId: string, event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.activeWarning.set(fieldId);
  }

  hideFieldWarning() {
    this.activeWarning.set(null);
  }

  toggleMobileMenu() {
    this.mobileMenuOpen.update(open => !open);
  }

  closeMobileMenu() {
    this.mobileMenuOpen.set(false);
  }

  toggleHelpSidebar(): void {
    const newValue = !this.authService.helpSidebarVisible();
    this.authService.updateHelpSidebarPreference(newValue).subscribe({
      error: (err) => console.error('Failed to save help sidebar preference:', err)
    });
  }

  goHome() {
    this.router.navigate(['/']);
  }

  /**
   * Validiert AI-Request Parameter
   * Prüft ALLE erforderlichen Felder auf einmal
   * @param scenarioIndex Optional: bei Szenario 3 wird ein anderes Feld validiert
   * @returns { valid: boolean; missingFields: string[] }
   */
  private validateAiRequestParameters(
    calculationId: number | null | undefined,
    country: string | null | undefined,
    city: string | null | undefined,
    postalcode: string | null | undefined,
    street: string | null | undefined,
    objectSize: string | null | undefined,
    creditTermYears?: string | null | undefined,
    scenarioIndex?: 1 | 2 | 3
  ): { valid: boolean; missingFields: string[] } {
    const missingFields: string[] = [];

    if (!calculationId) {
      missingFields.push('Gespeicherte Berechnung');
    }
    
    if (!country) missingFields.push('Land');
    if (!city || !postalcode) missingFields.push('Stadt/Postleitzahl');
    if (!street) missingFields.push('Straße');
    if (!objectSize) missingFields.push('Objektgröße');
    
    // Für Szenario 3: validiere "Veränderter Jahreszins effektiv (%)" statt Kreditlaufzeit
    if (scenarioIndex === 3) {
      if (creditTermYears !== undefined && !creditTermYears) {
        missingFields.push('"Veränderter Jahreszins effektiv (%)"');
      }
    } else if (creditTermYears !== undefined && !creditTermYears) {
      missingFields.push('Kreditlaufzeit');
    }

    return {
      valid: missingFields.length === 0,
      missingFields
    };
  }

  /**
   * Fragt die KI nach Nettokaltmiete pro m²
   * Parameter: Land, Postleitzahl, Straße, Objektgröße in m²
   */
  askAiRent() {
    // Feature Check
    if (!this.subscriptionService.hasFeature('AI')) {
      this.showToast('Diese Funktion ist in deinem aktuellen Abonnement nicht verfügbar. Aktualisiere dein Abonnement, um diese Funktion freizuschalten.', 'warning');
      return;
    }

    this.isLoadingAi.set(true);
    this.aiError.set('');

    // Validiere ALLE erforderlichen Parameter auf einmal
    const validation = this.validateAiRequestParameters(
      this.currentCalculationId(),
      this.country(),           // Kein Default!
      this.city(),
      this.postalCode(),
      this.address(),
      this.objectSizeInput()
    );

    if (!validation.valid) {
      this.isLoadingAi.set(false);
      
      // Unterschiedliche Meldung für neue (nicht gespeicherte) Berechnungen
      const message = validation.missingFields.includes('Gespeicherte Berechnung')
        ? 'Speichere deine Berechnung zuerst, um die KI-Funktion zu nutzen.'
        : `Um die KI-Schätzung zu berechnen, muss: ${validation.missingFields.join(', ')} eingetragen werden.`;
      
      this.showToast(message, 'error');
      return;
    }

    const request = {
      promptName: 'erwarteterMietpreisInQm',
      parameters: {
        country: this.country(),
        city: this.city(),
        postalcode: this.postalCode(),
        street: this.address(),
        objectSizeInQm: this.objectSizeInput(),
        constructionYear: this.constructionYearInput()
      },
      // Caching-Parameter: wenn CalculationId gesetzt, wird der Wert mit Hash gecacht
      calculationId: this.currentCalculationId() || undefined,
      cacheFieldName: 'NettoKaltmiete'
    };

    this.aiService.askAi(request).subscribe({
      next: (response) => {
        this.isLoadingAi.set(false);

        if (response.success && response.answer) {
          const parsed = this.aiService.parseAiAnswer(response.answer);
          const numValue = this.aiService.parseValue(parsed.value);

          if (numValue !== null) {
            // Set rental price directly and let Effects handle the calculations
            // DO NOT call updateRentalPrice() to avoid race conditions
            this.rentalPriceInput.set(numValue.toString().replace('.', ','));
            
            // Zeige Toast mit KI-Schätzung (EUR für Nettokaltmiete anzeigen)
            const displayValue = response.answer.includes(' EUR') ? `${parsed.value} EUR` : parsed.value;
            this.showToast(`KI-Schätzung: ${displayValue} - ${parsed.reason}`, 'ai');
          } else {
            this.aiError.set('KI-Antwort konnte nicht verarbeitet werden');
          }
        } else {
          this.aiError.set(response.error || 'Fehler bei KI-Anfrage');
        }
      },
      error: (error) => {
        this.isLoadingAi.set(false);
        this.showToast('KI-Schätzung momentan nicht verfügbar. Bitte später versuchen.', 'error');
        console.error('KI-Fehler:', error);
      }
    });
  }

  /**
   * Fragt die KI nach Wertsteigerung pro Jahr in Prozent
   * @param scenarioIndex Index des Szenarios (1, 2 oder 3)
   */
  /**
   * Fragt die KI nach Wertsteigerung für ALLE 3 Szenarien
   * Generiert einen Hash OHNE years-Parameter
   * Füllt alle 3 Felder mit dem gleichen KI-Wert
   */
  askAiAppreciationAllScenarios() {
    // Feature Check
    if (!this.subscriptionService.hasFeature('AI')) {
      this.showToast('Diese Funktion ist in deinem aktuellen Abonnement nicht verfügbar. Aktualisiere dein Abonnement, um diese Funktion freizuschalten.', 'warning');
      return;
    }

    this.isLoadingAiAppreciationAllScenarios.set(true);
    this.aiError.set('');

    // Validiere ALLE erforderlichen Parameter (OHNE creditTermYears!)
    const validation = this.validateAiRequestParameters(
      this.currentCalculationId(),
      this.country(),
      this.city(),
      this.postalCode(),
      this.address(),
      this.objectSizeInput()
    );

    if (!validation.valid) {
      this.isLoadingAiAppreciationAllScenarios.set(false);
      
      // Unterschiedliche Meldung für neue (nicht gespeicherte) Berechnungen
      const message = validation.missingFields.includes('Gespeicherte Berechnung')
        ? 'Speichere deine Berechnung zuerst, um die KI-Funktion zu nutzen.'
        : `Um die KI-Schätzung zu berechnen, muss: ${validation.missingFields.join(', ')} eingetragen werden.`;
      
      this.showToast(message, 'error');
      return;
    }

    const request = {
      promptName: 'erwarteteWertsteigerungProJahrInProzent',
      parameters: {
        country: this.country(),
        city: this.city(),
        postalcode: this.postalCode(),
        street: this.address(),
        objectSizeInQm: this.objectSizeInput(),
        constructionYear: this.constructionYearInput()
        // WICHTIG: KEIN years-Parameter!
      },
      // Caching-Parameter: neuer generischer Cache-Feldname
      calculationId: this.currentCalculationId() || undefined,
      cacheFieldName: 'AppreciationRate'
    };

    // Zusätzliche Validierung
    if (!request.parameters.postalcode || !request.parameters.street) {
      this.isLoadingAiAppreciationAllScenarios.set(false);
      this.showToast(
        `KI-Funktion erfordert: Stadt/Postleitzahl, Straße`,
        'error'
      );
      return;
    }

    this.aiService.askAi(request).subscribe({
      next: (response) => {
        this.isLoadingAiAppreciationAllScenarios.set(false);

        if (response.success && response.answer) {
          const parsed = this.aiService.parseAiAnswer(response.answer);
          const numValue = this.aiService.parseValue(parsed.value);

          if (numValue !== null) {
            const formattedValue = numValue.toString().replace('.', ',');
            
            // Füllt alle 3 Szenarien mit dem gleichen Wert
            this.scenario1AppreciationRateInput.set(formattedValue);
            this.scenario2AppreciationRateInput.set(formattedValue);
            this.scenario3AppreciationRateInput.set(formattedValue);
            
            // Zeige Toast mit KI-Schätzung
            this.showToast(`KI-Schätzung (alle Szenarien): ${parsed.value}% - ${parsed.reason}`, 'ai');
          } else {
            this.showToast('KI-Antwort konnte nicht verarbeitet werden', 'error');
          }
        } else {
          this.showToast(response.error || 'Fehler bei KI-Anfrage', 'error');
        }
      },
      error: (error) => {
        this.isLoadingAiAppreciationAllScenarios.set(false);
        this.showToast('KI-Schätzung momentan nicht verfügbar. Bitte später versuchen.', 'error');
        console.error('KI-Fehler:', error);
      }
    });
  }
}

interface AddressSuggestion {
  display_name: string;
  lat: string;
  lon: string;
  address?: {
    road?: string;
    postcode?: string;
    city?: string;
    country?: string;
  };
}
